// Small pure helpers for the Avatars tab, extracted so onboarding UX rules are
// unit-testable without standing up the full tab (query client + API mocks).

import type {
  AvatarLoad,
  EnvIngestionFailedGroup,
  FleetAvatar,
  FleetAvatarGroup,
} from "@/lib/types";
import { COUNTRIES } from "./countries";

// A never-linked avatar (status='new') is being connected for the first time,
// so the action button reads "Connect"; everything else is a "Relink".
export function relinkActionLabel(status: string): "Connect" | "Relink" {
  return status === "new" ? "Connect" : "Relink";
}

// Stale-status clobber guard (2026-07-12 S5_T_1): an edit form holds the
// status it was seeded with, which can go stale while the dialog is open
// (a relink completing flips new→active server-side). Only send `status`
// when the operator actually changed it, and pair it with `status_baseline`
// (the seeded value) so the backend drops the change if the row moved on.
// The backend PUT whitelist only accepts operator-settable lifecycle values
// (new/disconnected/blocked); active/cooldown/stale/deleted are derived from
// real collection + the heartbeat and 400 when hand-set. The form select must
// only offer values that can save — plus the row's current status (first) so
// an avatar in a derived state still renders truthfully; re-selecting it is a
// no-op (editStatusFields omits an unchanged status from the payload).
// IR-1351 r6: 'obsolete' is operator-settable — "we no longer have this handset"
// is a fact about hardware that no live signal can derive, so it has to be
// hand-set like new/disconnected/blocked. The backend whitelist widened to match.
export function settableStatusOptions<S extends string>(seeded: S | undefined): S[] {
  // IR-1700 (Yaniv in-session 2026-09-30: "Allow manual park on V2"):
  // 'ready_to_use' used to be refused here as a DERIVED-only value (r11,
  // before IR-1516 existed). IR-1516 made it a real, stored, operator-set
  // flag (`views_fleet.py SETTABLE_STATUSES`, backend PUT whitelist since
  // 2026-09-14) — the backend is the authority on eligibility
  // (`_ready_to_use_refusal`: WhatsApp/Telegram only, zero groups only) and
  // 400s an ineligible PUT regardless of what this list offers, exactly
  // like every other value here. Hiding it was never a safety net; it was
  // the reason no avatar anywhere had ever been parked (0 rows, dev and
  // prod, 2026-09-30 measurement).
  // IR-1769 (O-8): "disconnected" is no longer a hand-set choice; the Stop button
  // sends "stopped" and Relink sends a force_relink Disconnected.
  const base = ["new", "blocked", "obsolete", "ready_to_use"] as S[];
  if (seeded === undefined || base.includes(seeded)) return base;
  return [seeded, ...base];
}

// Send `status` only when the operator changed it away from the DISPLAYED
// (canonical) value; pair it with the RAW baseline so the backend clobber-guard
// compares raw-to-raw. displayStatus = what the dropdown showed (canonical);
// baselineStatus = the raw db_status (falls back to displayStatus when the API
// didn't provide db_status).
//
// This rule is correct FOR A LABEL and stays as-is. It is NOT sufficient for a
// COMMAND — see `canIssueStop` below for why stopping collection needed its own
// control rather than a value in this select.
// Two type params, not one (IR-1688): `baselineStatus` is seeded from the raw
// `db_status` column, whose domain is now wider than the settable/displayed
// one — it can read "ready_to_use" (IR-1516), a value nobody can pick from the
// status select and this function never assigns to `status`. Sharing a single
// `S` across all three params would let that literal leak into the `status`
// field's inferred type even though only `formStatus` is ever returned there.
export function editStatusFields<S extends string, B extends string = S>(
  formStatus: S,
  displayStatus: S,
  baselineStatus: B,
): { status?: S; status_baseline?: B } {
  if (formStatus === displayStatus) return {};
  return { status: formStatus, status_baseline: baselineStatus };
}

// IR-1287 r2 — stopping collection is a COMMAND, not a label, and it needs its
// own control. Setting `disconnected` makes the backend emit the
// `scrape_deactivations/` sidecar that drops the agent from the VM scheduler
// roster; expressing that through the status select made it UNREACHABLE on
// exactly the avatars it exists for:
//
//   * `status` is DERIVED, so a quiet or dead avatar already READS
//     `disconnected` in the dropdown. Re-picking it is "unchanged", so
//     `editStatusFields` sends nothing — no transition, no sidecar, no stop.
//   * A native <select> does not even fire `onChange` when you re-pick the
//     value it already holds, so "did the operator touch it" cannot rescue the
//     select either.
//
// Observed on production 2026-08-05 (S1_T1 +420728613563): status badge
// DISCONNECTED, collection chip Active, and no way to issue the stop from the
// UI at all. A button carries the intent unambiguously and leaves IR-1235's
// silent-stop hazard closed — a name-only edit cannot press a button.
//
// Offered while collection is running (`running`/`running_no_data`), for a
// disconnected avatar (`disconnected` — parking a dead account is a legitimate
// operator move), or when a previous stop failed on the VM (`stop_failed`,
// worth retrying). Hidden once the stop is issued (`stop_pending`) or confirmed
// (`stopped`) — re-issuing then is a no-op that would only clear the
// confirmation stamp. IR-1327 r6: the three offered non-stop states are exactly
// the split of the old `active`, so the button's reach is unchanged.
//
// IR-1351: 'not_initiated' is deliberately absent. It forked out of 'active',
// which DID offer the button, so this is a behaviour change and an intended
// one — an avatar that was prepared and never linked has no collection to stop,
// and the sidecar that a stop emits would drop an agent that was never on the
// VM roster in the first place.
//
// IR-1720: both resume states offer it too. `resume_pending` — an operator may
// change their mind while a resume is still in flight, and the avatar is by
// then stored `active`, so nothing else on the row could stop it.
// `resume_failed` — stopping is the way OUT of a failed resume: it cancels the
// collection machine's own retrying and puts the row back to a plain operator
// stop, from which Resume can be pressed again (the manual retry).
//
// IR-1720 (Signal follow-up, 2026-10-05): never on a Signal row, in any state.
// The stop never reaches the collection machine for Signal — the press only
// changed the record, and the row then read DISCONNECTED / "Stop pending" for
// good (found on production). Harel's decision: hide it, with no replacement
// control. Only `signal` is excluded; a row with no platform keeps the button
// it always had.
export function canIssueStop(
  av: { platform?: string | null | undefined; status?: string | null | undefined },
  state: CollectionState,
): boolean {
  if (isSignalPlatform(av.platform)) return false;
  // IR-1351 r8, kept through IR-1769 r5: an Obsolete handset is never offered
  // Stop — the PUT behind the button writes `disconnected` and would silently
  // destroy the Obsolete mark. The old `retired` state carried this; now that an
  // obsolete row with no stop on record reads Unknown, the status says it.
  if (av.status === "obsolete" && state !== "stop_failed") return false;
  return (
    state === "running" ||
    state === "running_no_data" ||
    // IR-1769 r5: `unknown` and `not_collecting` are NOT stop states (a stop
    // is neither requested nor on record), so Stop stays offered exactly where
    // the old `disconnected` word offered it.
    state === "unknown" ||
    state === "not_collecting" ||
    state === "stop_failed" ||
    state === "resume_pending" ||
    state === "resume_failed"
  );
}

// IR-1720 — who gets the "Resume collection" button. The rule is the backend's
// own (it answers 409 to anything else), mirrored here so the control is only
// offered where pressing it can work:
//
//   * the row READS as an operator stop (`stop_pending` / `stopped` /
//     `stop_failed`), AND
//   * the STORED status is `disconnected` — `db_status`, deliberately not the
//     derived `status`. The `operator_stop` branch of `collectionState` also
//     covers blocked / obsolete / ready_to_use rows that were stopped on the
//     way, and Harel's scope decision (2026-10-05) is that Resume undoes a
//     PLAIN stop only: resuming a blocked or retired account would silently
//     overwrite the mark an operator set on purpose. The derived `status`
//     cannot tell these apart — every operator-stopped row derives to
//     `disconnected`.
//   * the provenance is `operator_stop` — a `manual` phone edit or a dead
//     session also stores `disconnected`, and neither is undone by a start
//     request (they need a relink).
//   * the platform is Telegram, WhatsApp or Signal. Signal has no stop/start
//     channel, so for it the backend treats Resume as a record-only undo: it
//     clears a stop mark that never reached the collection machine. It is
//     offered because `canIssueStop` used to put that mark on Signal rows and
//     nothing could take it off again.
//
// `resume_failed` does NOT offer it, on purpose: that row is no longer stored
// `disconnected` (Resume moved it to `active`), the collection machine keeps
// retrying the start by itself, and the manual retry is Stop collection →
// Resume collection — `canIssueStop` above offers the first half.
//
// On `stop_failed` both buttons show: retry the stop, or give up and resume.
export function canIssueResume(
  av: {
    platform?: string | null | undefined;
    db_status?: string | null | undefined;
    status_source?: string | null | undefined;
  },
  state: CollectionState,
): boolean {
  if (state !== "stop_pending" && state !== "stopped" && state !== "stop_failed") return false;
  // IR-1769 (O-10): the stored word `stopped`, or the legacy shape until 0073.
  if (av.db_status !== "stopped" && av.db_status !== "disconnected") return false;
  if (av.status_source !== "operator_stop") return false;
  const platform = (av.platform ?? "").toLowerCase();
  return platform === "telegram" || platform === "whatsapp" || platform === "signal";
}

// IR-1769 (O-47): is this avatar Stopped? The stored word, or the legacy
// `disconnected` + `operator_stop` shape until migration 0073 converts it. An
// `obsolete` / `blocked` row that merely carries the stop provenance is NOT
// Stopped: Stopped is an Avatar Status of its own.
export function isStoppedAvatar(av: {
  status?: string | null | undefined;
  db_status?: string | null | undefined;
  status_source?: string | null | undefined;
}): boolean {
  if (av.status === "stopped" || av.db_status === "stopped") return true;
  return av.db_status === "disconnected" && av.status_source === "operator_stop";
}

// IR-1769 (O-47, Rotem 64931 "show who, why, when"): the visible Stopped detail.
// The time is `collection_stop_requested_at` (the receipt of the stop) and
// nothing else: `status_changed_at` is overwritten by a Resume, so a fallback
// to it would show a wrong time with confidence. A NULL says so in words.
// `fmt` is the tab's date formatter, passed in so this stays a pure helper.
export function stoppedDetail(
  av: {
    status?: string | null | undefined;
    db_status?: string | null | undefined;
    status_source?: string | null | undefined;
    stopped_by?: string | null | undefined;
    stop_reason?: string | null | undefined;
    collection_stop_requested_at?: string | null | undefined;
    collection_stopped_at?: string | null | undefined;
    collection_stop_error?: string | null | undefined;
    collection_resume_requested_at?: string | null | undefined;
  },
  fmt: (iso: string) => string,
): string | null {
  const stopped = isStoppedAvatar(av);
  // IR-1769 r5 (65104 para 2): an Obsolete / Deleted row that still carries a stop
  // request (operator_stop provenance + a request time, no newer resume) shows the same
  // historic line. That is a separate predicate: it does not make the row Stopped.
  const resumedSince =
    Date.parse(av.collection_resume_requested_at ?? "") >=
    Date.parse(av.collection_stop_requested_at ?? "");
  const hasRequest =
    !stopped &&
    (av.status === "obsolete" ||
      av.status === "deleted" ||
      av.db_status === "obsolete" ||
      av.db_status === "deleted") &&
    av.status_source === "operator_stop" &&
    !!av.collection_stop_requested_at &&
    !resumedSince;
  if (!stopped && !hasRequest) return null;
  // IR-1769 G2 r4 (Rotem 65104 para 2, her words): a historic stop shows its existing
  // request time with "Requested by: Not recorded" and "Reason: Not recorded". The request
  // time is when the stop was asked for, never the collector's confirmation (the ack
  // below is the only confirmation), so Collection stays Stop pending until one arrives.
  const verb = stopped ? "Stopped" : "Stop requested";
  const who = av.stopped_by ? `${verb} by ${av.stopped_by}` : `${verb}, Requested by: Not recorded`;
  const why = av.stop_reason ? `reason: ${av.stop_reason}` : "Reason: Not recorded";
  const when = av.collection_stop_requested_at
    ? `at ${fmt(av.collection_stop_requested_at)}`
    : "time not recorded";
  const ack = av.collection_stop_error
    ? `collector could not apply it: ${av.collection_stop_error}`
    : av.collection_stopped_at
      ? `collector confirmed ${fmt(av.collection_stopped_at)}`
      : "collector has not confirmed yet";
  return `${who}, ${why}, ${when}; ${ack}`;
}

// IR-1720 — the one platform whose stop / resume never reaches the collection
// machine. Shared by the two rules above and by the row's wording, so the
// button gate and the sentences beside it cannot disagree about what counts
// as Signal.
export function isSignalPlatform(platform: string | null | undefined): boolean {
  return (platform ?? "").toLowerCase() === "signal";
}

// IR-1720 — the sentence shown when a Resume press is refused. The tab had no
// error surface for a row command at all (a failed Stop is silent to this day),
// and a control that restarts collection cannot fail quietly: the operator
// would wait on a "Resume pending" that was never going to appear.
//
// The backend's `error` strings are written to be shown, so they lead; what
// this adds is the NEXT STEP, because the server sentence alone says what went
// wrong but not what to do:
//   502 — the start request could not be sent; the backend rolled the row back
//         to stopped, so the button is still there and pressing again is safe.
//   409 — the row is no longer a plain operator stop (someone else resumed it,
//         or it was blocked/relinked meanwhile); the list is refetched by the
//         caller, so the row now shows where it really stands.
//   404 — this backend has no Resume action yet (OPS shipped ahead of it).
//
// Duck-typed on `status` / `body` rather than `instanceof ApiError` so this
// file stays free of the api module (and of the auth store it pulls in).
export function resumeErrorMessage(name: string, err: unknown): string {
  const e = (err ?? {}) as { status?: unknown; body?: unknown };
  const status = typeof e.status === "number" ? e.status : null;
  const serverText =
    e.body !== null &&
    typeof e.body === "object" &&
    typeof (e.body as { error?: unknown }).error === "string"
      ? (e.body as { error: string }).error.trim()
      : "";
  const reason = (serverText || "Could not resume collection.").replace(/([^.!?])$/, "$1.");
  const nextStep =
    status === 502
      ? "The avatar is still stopped — press Resume collection again."
      : status === 409
        ? "The list was reloaded to show where this avatar stands now."
        : status === 404
          ? "This environment does not support Resume collection yet."
          : "Nothing was changed — press Resume collection again.";
  return `${name}: ${reason} ${nextStep}`;
}

// Onboarding needs name + platform plus at least one account identifier — a
// phone (telethon/whatsapp-web onboarding) OR a platform-native id. The native-id
// fallback lets phone-less WhatsApp avatars (identified by JID/configCode) be
// saved so they can be blacklisted from ingestion.
export function canSubmitAvatarForm(f: {
  name: string;
  platform: string;
  phone_number: string;
  platform_native_id?: string;
}): boolean {
  return Boolean(
    f.name?.trim() &&
      f.platform?.trim() &&
      (f.phone_number?.trim() || f.platform_native_id?.trim()),
  );
}

// IR-1226 P1: operator-facing status labels (PRD vocabulary). blocked/cooldown
// render as "Suspended" per the epic's avatar status list; the raw canonical
// value stays visible in the pill tooltip. Both 'active' (legacy canonical
// name) and 'connected' (avatar-connected-status rename) map to Connected so
// the label layer survives the backend vocabulary transition.
export function statusLabel(status: string): string {
  switch (status) {
    case "active":
    case "connected":
      return "Connected";
    case "stale":
      return "Stale";
    case "disconnected":
      return "Disconnected";
    case "stopped":
      return "Stopped";
    case "new":
      return "New";
    case "blocked":
    case "cooldown":
      return "Suspended";
    case "deleted":
      return "Deleted";
    case "obsolete":
      return "Obsolete";
    // IR-1351 r11 (Rotem): "Create another status for Avatars which are
    // connected with '0' groups - 'Ready to Use'". The API never sends this word
    // as `status`: it is read from the STORED lifecycle value `db_status` (IR-1688,
    // see `isReadyToUseAvatar`) and applied here, and a derived `disconnected`
    // outranks it (IR-1700 F1). It reaches this function through `effectiveStatus`,
    // which is why the pill, the status chip, the KPI bucket and the sort all
    // pick it up from one edit rather than five.
    case "ready_to_use":
      return "Ready to Use";
    default:
      return status;
  }
}

// IR-1351 r11 — the derivation, and the ONLY place it happens. A linked avatar
// that has been assigned no groups is not "collecting nothing", it is spare
// capacity waiting for an assignment: nothing is wrong with it and nothing is
// expected of it. Before this it was indistinguishable from a Connected avatar
// that had genuinely gone quiet, so it landed in the amber "Running, no data"
// slice — the one Rotem added the ring to watch. Idle stock sitting in the
// alarm bucket is precisely the false positive the ring exists to remove.
//
// Keyed off `isActiveStatus`, not the string 'connected', so it survives the
// active→connected vocabulary transition the same way `statusLabel` does.
// IR-1351 r12 — `last_data_at == null` is the third clause, and it is what makes
// this status safe to act on. Rotem, 2026-08-14: "Ready to use now is coupled
// with all weird statuses ... running & ingesting is simply not true".
//
// The row she is looking at is real, but the false value is `group_count`, not
// the Collection/Ingestion pair. `group_count` counts rows in the fleet
// `avatar_groups` table, which the backend documents as an UNDER-REPORT of the
// fleet — `avatar_status.AvatarStatusInput.has_coverage`: "When False (no group
// rows — tracker.json under-reports the fleet, root cause E), we cannot verify
// collection", and `backfill_avatar_groups_from_items`: "the daily reconcile only
// sees tracker.json — CURRENT scrape coverage — so groups that are no longer
// scraped never get an avatar mapping".
//
// The Ingestion column, by contrast, runs off an INDEPENDENT and stronger signal:
// since the 2026-07-19 misattribution fix, items are credited to the avatar that
// actually collected them by parsing the scrape BATCH FOLDER NAME (which embeds
// the agent), explicitly not via the `avatar_groups` join. So on her screenshot
// S2_T2 and S4_T3 rendering "Running / Ingesting" is positive evidence that those
// avatars ARE collecting — they have groups; we have no rows for them.
//
// ⇒ Applying her rule on `group_count` alone would blank the one column telling
// the truth: a working avatar would read "Not initiated / Not applicable" while
// its batches kept landing. Gating on `last_data_at` — which the backend defines
// as "ISO string or null (no data ever observed)" — restricts the status to
// avatars that have genuinely never collected anything, which is what "prepared
// but never used" means. Her column rule is then correct by construction, and
// the avatars that are demonstrably collecting stay Connected and keep saying so.
// IR-1663 (Rotem, comment 62958) — "never collected anything" is a claim about
// the COLLECTOR, not about this environment's own ingestion. `last_data_at` is
// the Ingestion axis (per-environment: did content land HERE); `last_collection_at`
// is the Collection axis (one fact, the same on dev and prod, because its true
// source — the V2 wrapper heartbeat blob, or item-landing as a documented V1
// approximation — is itself read cross-environment; see wrapper_heartbeat.py /
// avatar_activity.py). Reading `last_data_at` here would mark an avatar "Ready
// to Use" (never touched) whenever its content happens to land in the OTHER
// environment, which is exactly the axis-crossing bug this ticket fixes.
// IR-1688 (Yaniv/Rotem, IR-1516 comment 62449 production incident, 2026-09-16):
// this function used to COMPUTE "ready to use" from group_count/last_collection_at
// — an inference — and Rotem caught it live: six avatars the page called Ready to
// Use were still being collected every round. "The page works it out while you
// look at it ... It is not stored anywhere, and nothing outside that screen ...
// can see it." IR-1516 had already built the real thing: an operator (or the
// daily discovery wake) sets `avatars.status = 'ready_to_use'` and THAT stops
// collection and alerts. The two were never the same value — this function
// closes that gap by reading the stored one, only.
//
// It reads `db_status`, not `status`. Every derive_* tier
// (avatar_status.py `is_operator_stopped`) treats an operator-stop as
// DISCONNECTED and OVERWRITES the canonical `status` the API returns with that
// value, for every stop status including `ready_to_use` — so a genuinely
// parked avatar is sent to iris-ops as `status: "disconnected"` and the mark
// survives only in the raw lifecycle column, `db_status` (see its doc comment
// in `src/lib/types.ts`). Checking `status` here would never fire.
export function isReadyToUseAvatar(av: {
  // Unused by this function — every derive_* tier collapses an operator-stop
  // avatar's canonical `status` to "disconnected", so `ready_to_use` never
  // appears here. Named anyway so callers can pass a real API row (which
  // always carries both fields) as a literal without an excess-property error.
  status?: string | null | undefined;
  db_status?: string | null | undefined;
}): boolean {
  return (av.db_status ?? "").toLowerCase() === "ready_to_use";
}

// IR-1225: an avatar counts as "active" for the KPI tile when its derived status
// is Connected. The canonical backend value is "connected" (avatar-connected-status
// rename); "active" is the legacy value. Mirror statusLabel's both-map-to-Connected
// handling so the Active tile matches the row pills instead of reading 0.
export function isActiveStatus(status: string): boolean {
  return status === "connected" || status === "active";
}

// IR-1327 r3 (Rotem: "I see 57 and not 58 — please add 'disconnected' status to
// the metric"). The KPI strip showed Total / Connected / Stale, so on prod it
// read 57 / 33 / 5 and left 19 avatars represented by no tile at all — the
// reader is left to work out whether the missing rows are a fourth state or a
// counting bug. This ticket has now produced that same "which population is this
// number over?" confusion three times, so the tiles are built from a TOTAL
// PARTITION instead of three independent predicates: every avatar maps to
// exactly one bucket, therefore connected + stale + disconnected + other always
// equals the Total tile, by construction rather than by coincidence.
//
// `other` is New + Suspended (blocked/cooldown) — and Deleted on the one screen
// that shows it, when the operator picks the Deleted status chip.
//
// IR-1351 r6: `obsolete` gets its OWN bucket rather than joining `other`.
// Rotem asked for these to be marked AND to be "not part of the fleet", so they
// must not be counted inside the Total the fleet tiles partition — but a number
// that is merely missing is exactly the "which population is this over?"
// confusion described above. Its own bucket keeps the invariant intact
// (connected + stale + disconnected + other === Total) while the excluded
// population stays visible in its own tile.
//
// IR-1351 r11: `ready` is carved OUT of `connected` rather than added beside it.
// Rotem asked for the count as a box, and the boxes are a partition she adds up
// (IR-1327 r3 was that complaint verbatim) — a tile counting a subset of a
// number already on screen would make five tiles plus a ring visibly overshoot
// Total. Carved out, the arithmetic still closes: ready + connected(the ring) +
// stale + disconnected + other === Total.
export type AvatarKpiBucket =
  | "connected"
  | "ready"
  | "stale"
  | "disconnected"
  // IR-1769 (O-13): a deliberate operator stop, its own tile. Closes the
  // partition: ready + connected + stale + disconnected + stopped + other = Total.
  | "stopped"
  | "other"
  | "obsolete";

// `status` here is the DERIVED display status (`effectiveStatus`), not the raw
// API column — 'ready_to_use' only ever exists after that derivation.
export function avatarKpiBucket(status: string): AvatarKpiBucket {
  if (status === "ready_to_use") return "ready";
  if (isActiveStatus(status)) return "connected";
  if (status === "stale") return "stale";
  if (status === "disconnected") return "disconnected";
  if (status === "stopped") return "stopped";
  if (status === "obsolete") return "obsolete";
  return "other";
}

// IR-1351 r6: the fleet population — everything the Total tile and the four
// status tiles are computed over. An obsolete avatar is still LISTED (that is
// the mark Rotem asked for) but is not fleet, so every fleet number is taken
// over this filter rather than over the raw row count.
export function isFleetAvatar(status: string): boolean {
  return avatarKpiBucket(status) !== "obsolete";
}

// ─── IR-1727 (Rotem: header "63 of 96 avatars" though only 64 are real) ───
//
// The backend's own retirement line — `models_fleet.RETIRED_AVATAR_STATUSES =
// {deleted, obsolete}` (iris-backend 6642693c8) — was never mirrored on this
// side. `isFleetAvatar` above only ever excluded 'obsolete': it is keyed off
// the DERIVED display status (`avatarKpiBucket`), and 'deleted' is not a
// member of that bucket enum at all — a lifecycle fact, not a live state (see
// `isAvatarVisibleForStatusFilter`'s comment). So a soft-deleted avatar that
// derives to 'connected' (IR-1327 r2's "resurrected" shape: `status:
// 'connected', db_status: 'deleted'`) was never excluded from anything on this
// screen — it just happened not to show up BECAUSE the fetch itself drops
// deleted rows by default. The moment an operator opens the Deleted chip
// (`avatarFilters.include_deleted = true` in `AvatarsTab`), that same row
// starts counting as Connected in the KPI strip and as part of the headline
// total, which is exactly the "too many avatars" complaint.
//
// ONE constant, mirroring the backend's, so "what counts as retired" cannot
// drift into a second scattered set of 'deleted' / 'obsolete' string literals.
// Checked against the RAW lifecycle value (`db_status`, falling back to
// `status` for a row that never carried the column) — never the derived
// display status — because the derived status is deliberately allowed to say
// something else (a deleted row rendering a green "Connected" pill is the
// IR-1327 r2 behaviour, not a bug; it is the COUNTING that must not be fooled
// by it).
export const RETIRED_AVATAR_STATUSES: ReadonlySet<string> = new Set(["deleted", "obsolete"]);

/** The raw lifecycle value to judge retirement by: `db_status` when the row
 * carries one, else `status` (a payload/fixture that never set `db_status`
 * behaves exactly as if the two columns agreed, which is the pre-existing
 * assumption every other helper in this file already makes). */
export function avatarLifecycleStatus(av: {
  db_status?: string | null | undefined;
  status?: string | null | undefined;
}): string {
  return (av.db_status ?? av.status ?? "").toLowerCase();
}

/** True for a deleted OR obsolete avatar, judged on lifecycle — see above. */
export function isRetiredAvatar(av: {
  db_status?: string | null | undefined;
  status?: string | null | undefined;
}): boolean {
  return RETIRED_AVATAR_STATUSES.has(avatarLifecycleStatus(av));
}

// IR-1474 (Rotem, 2026-09-02): "Please set default sorting — Primary Status:
// Disconnected, Connected, Ready to use, Obsolete." The tab had no default sort
// at all before this: `useClientSort` was called without its `defaultSort`
// argument, so the rows arrived in whatever order the API sent, which is
// `Avatar.Meta.ordering = ['name']` — name A-Z with status ignored.
//
// Ranked BY BUCKET rather than by a second hand-written list of statuses. The
// ticket names four of the nine display statuses; keying off `avatarKpiBucket`
// is what gives the other five a defensible position and what stops this table
// from drifting away from the KPI tiles and having the sort disagree with the
// strip about what is wrong — the contradiction IR-1327 r3 spent a round
// fixing. A status added to the bucket function tomorrow is ranked for free.
//
// The two unnamed placements worth stating:
//   * `stale` sits between Disconnected and Connected. It is a failure (a
//     session that has gone quiet), not spare capacity, so it belongs with the
//     rows that need attention rather than after the healthy ones. On prod it
//     is carved out of the 47 rows reading `active`, so it is a real block.
//   * `other` (blocked / cooldown / new / deleted) sits below Ready to Use and
//     above Obsolete: an avatar nobody has onboarded has not failed at anything
//     (the reasoning `isAvatarFailing` already applies to `new`), but it is
//     still fleet, which a retired handset is not.
//
// NOTE this rank is deliberately ATTENTION-FIRST, the opposite of the
// healthiest-first convention `ingestionSortRank` / `collectionSortRank` follow.
// Those two are columns an operator sorts BY; this one is the order the tab
// LANDS in, and Rotem asked for the trouble at the top. The consequence is that
// one ascending click on Status reads worst-to-best while the same click on
// Collection reads best-to-worst.
const STATUS_SORT_RANK: Record<AvatarKpiBucket, number> = {
  disconnected: 0,
  // IR-1769 (O-15): a deliberate stop is attention-worthy (someone must press
  // Resume) but less urgent than a session that died on its own.
  stopped: 1,
  stale: 2,
  connected: 3,
  ready: 4,
  other: 5,
  obsolete: 6,
};

// `status` is the DERIVED display status (`effectiveStatus`), not the raw API
// column — same input `avatarKpiBucket` takes, so 'ready_to_use' and the legacy
// 'active' spelling are both handled by that one function.
export function statusSortRank(status: string): number {
  return STATUS_SORT_RANK[avatarKpiBucket(status)];
}

// IR-1515 (Rotem, 2026-09-07): "add Date for disconnected or sorted disconnected
// list - newest on top ... we lost 3 avatars over the weekend and I cant tell
// which!!!"
//
// A tiebreaker inside the status block IR-1474 already established, returning a
// NEGATED millisecond stamp so `useClientSort`'s always-ascending tiebreakers
// read newest-first. Null (never collected, or the backend has not shipped the
// column yet) keeps the hook's nulls-last behaviour, which is right: an avatar
// that never collected belongs at the bottom of the block, not the top.
//
// 🔑 SCOPED TO THE ATTENTION BUCKETS, and that asymmetry is deliberate. Recency
// answers "what just broke", which is a question only the failed rows raise —
// every Connected avatar collected minutes ago, so date-ordering them would
// shuffle the healthy block on every refresh and destroy the A-Z an operator
// uses to FIND a named avatar (IR-1474 item 2). Returning null outside those
// buckets leaves every other block ordered exactly as IR-1474 left it.
//
// It sits AHEAD of the platform tiebreaker rather than inside it: "newest on
// top" is a statement about the disconnected list as a whole, and grouping by
// platform first would bury Friday's Telegram loss under every WhatsApp row.
export function disconnectedRecencyRank(
  status: string,
  lastCollectionAt: string | null | undefined,
): number | null {
  const bucket = avatarKpiBucket(status);
  if (bucket !== "disconnected" && bucket !== "stale") return null;
  if (!lastCollectionAt) return null;
  const ms = Date.parse(lastCollectionAt);
  return Number.isNaN(ms) ? null : -ms;
}

// ─── Fleet mix donut (IR-1351 r9) — REMOVED in r10 ──────────────────────────
//
// r9 put a donut of the four fleet buckets beside the tiles. Rotem, one round
// later: "instead 'Fleet Mix' show Connected per collection status". The ring
// restated numbers the tiles already carried, so it was spending the one
// graphical slot on the question she could already answer. `fleetMixSlices` /
// `fleetMixColor` / `FLEET_MIX_*` are gone rather than left unused — a helper
// with no consumer reads as a feature that exists somewhere on the screen.
// Their replacements are `connectedCollectionSlices` / `connectedIngestionSlices`
// at the end of this file.

// IR-1351 item 4 — "failed" avatars, for the toolbar toggle Rotem asked for:
// "I meant user can filter failed avatars and view their proxy". The point is to
// narrow to the avatars worth investigating and read their Proxy column, so that
// N of them sharing one proxy is visible at a glance.
//
// Defined ON TOP of avatarKpiBucket rather than as its own list of statuses, so
// it cannot drift away from the KPI strip and have the tiles disagree with the
// filter about what is wrong — the exact class of contradiction IR-1327 r3 spent
// a round fixing.
//
// `new` is excluded deliberately: it has never been linked, so it has not failed
// — including it would bury real failures under avatars nobody has onboarded
// yet. `deleted` is not in the default view at all.
export function isAvatarFailing(status: string): boolean {
  const bucket = avatarKpiBucket(status);
  if (bucket === "connected") return false;
  // IR-1351 r11: same reasoning as `new` and `obsolete` — an avatar nobody has
  // given work to has not failed at it. Counting spare capacity as failed would
  // bury the real failures under the "Failed only" switch, which is the switch
  // Rotem uses to hunt a bad proxy.
  if (bucket === "ready") return false;
  // IR-1769 (O-14): a Stop is deliberate, like `obsolete` below; it is not a failure.
  if (bucket === "stopped") return false;
  if (bucket === "stale" || bucket === "disconnected") return true;
  // IR-1351 r6: `obsolete` is not a failure — we retired the handset on purpose.
  // Same reasoning as `new`: including it would bury real failures under
  // avatars nobody is expecting to collect.
  if (bucket === "obsolete") return false;
  // bucket 'other' = new | blocked | cooldown | deleted — only the suspended
  // pair counts as a failure.
  return status === "blocked" || status === "cooldown";
}

// Avatars screen status-filter visibility. `statusFilter` holds display labels
// (from statusLabel); an empty filter is the DEFAULT view. Default hides
// DELETED avatars — they only appear when "Deleted" is explicitly selected.
//
// IR-1327 r2: `dbStatus` (the raw lifecycle column) decides the Deleted axis
// when present, because a soft-deleted avatar can DERIVE to `connected` —
// `derive_avatar_status` deliberately lets Tier-1 item-landing outrank the
// `deleted` lifecycle value. Filtering the Deleted chip on the derived status
// alone hid 6 of prod's 11 deleted rows from the chip that exists to show them,
// while those same rows rendered a green CONNECTED pill in the default view.
// Deleted is a lifecycle fact, not a live state, so lifecycle wins here.
/**
 * IR-1610 r2 (Rotem, 2026-09-22): "show the default status-filter selections
 * as checked; adding Deleted must not make it look as though Deleted is the
 * only selected state."
 *
 * The defaults are SHOWN as checked, so they have to be a real selection —
 * not an empty array that means one thing to the predicate ("everything but
 * Deleted") and another to the chip ("nothing selected"). Every label the
 * Status chip offers except Deleted, in the chip's own order.
 */
export const DEFAULT_STATUS_FILTER: readonly string[] = [
  "New",
  "Ready to Use",
  "Connected",
  "Disconnected",
  // IR-1769 (O-17): Stopped is its own chip and is checked by default.
  "Stopped",
  "Suspended",
  "Obsolete",
  "Stale",
];

/** Set equality against the defaults, order independent. */
export function isDefaultStatusFilter(sel: readonly string[]): boolean {
  if (sel.length !== DEFAULT_STATUS_FILTER.length) return false;
  const have = new Set(sel);
  return DEFAULT_STATUS_FILTER.every((l) => have.has(l));
}

// Labels whose underlying lifecycle value is itself retired — the two spots
// `RETIRED_AVATAR_STATUSES` surfaces as a status-filter CHIP rather than a
// raw value.
const RETIRED_STATUS_LABELS: ReadonlySet<string> = new Set(["Obsolete", "Deleted"]);

// IR-1727 — the headline total ("X of Y avatars"). Before this, the footer's
// Y was `all.length`: the raw fetched array, which ALWAYS carries Obsolete
// rows (they are listed by default) and carries Deleted rows too the moment
// the Deleted chip is on (`include_deleted`). Neither is "real" fleet, so Y
// read high the moment either was in view — the "63 of 96 avatars" complaint
// (96 ⊇ 64 real + 14 obsolete + 18 deleted on prod's own numbers).
//
// Two regimes, chosen by what the STATUS filter alone is asking for (search,
// platform, etc. never change which regime applies):
//
//   * Status filter is EXACTLY one or more retired labels (Obsolete and/or
//     Deleted, nothing else — e.g. the operator picked only "Obsolete" to go
//     look at retired handsets): Y is the size of THAT selection — "the X of Y
//     under that filter must make sense" reads as "of the retired rows picked,
//     how many are shown", not "of the whole fleet", because the whole fleet
//     is not what is on screen.
//   * Anything else, including the untouched defaults (which DO include the
//     Obsolete label, because an obsolete avatar is still listed): Y is the
//     real fleet — every avatar whose lifecycle is not deleted or obsolete.
//     This is the number Rotem's "only 64 are real" is asking for, and it is
//     deliberately NOT "whatever the current status filter happens to admit"
//     — picking a platform or classification chip must not make Y wobble
//     between 64 and 78 depending on whether Obsolete rows happen to match.
type LifecycleLike = {
  db_status?: string | null | undefined;
  status?: string | null | undefined;
};

/** Is the status filter asking ONLY for retired rows (Obsolete and/or Deleted)? */
export function isRetiredOnlyStatusFilter(statusFilter: readonly string[]): boolean {
  return statusFilter.length > 0 && statusFilter.every((l) => RETIRED_STATUS_LABELS.has(l));
}

export function avatarHeadlineTotal(
  all: readonly LifecycleLike[],
  statusFilter: readonly string[],
): number {
  if (isRetiredOnlyStatusFilter(statusFilter)) {
    const wanted = new Set<string>(
      statusFilter.map((l) => (l === "Obsolete" ? "obsolete" : "deleted")),
    );
    return all.filter((av) => wanted.has(avatarLifecycleStatus(av))).length;
  }
  return all.filter((av) => !isRetiredAvatar(av)).length;
}

export interface AvatarHeadlineCounts {
  /** "X" — the real (non-retired) rows actually listed, or (retired-only
   * regime) the retired rows actually listed. Never exceeds `total`. */
  shown: number;
  /** "Y" — see `avatarHeadlineTotal`'s own comment for the two regimes. */
  total: number;
  /** Obsolete rows actually listed, for the "· N obsolete" suffix. Always 0
   * in the retired-only regime — that regime's own "X of Y" already says it. */
  obsoleteShown: number;
  retiredOnly: boolean;
}

// IR-1727 follow-up (orchestrator audit + Yaniv, 2026-10-06): "64 avatars"
// alone, sitting above a 78-row table (64 real + 14 obsolete, since Obsolete
// is still LISTED by default), read like a second instance of the very bug
// this ticket exists to fix — a headline number lower than the table beneath
// it. Yaniv's call: show both — "64 avatars · 14 obsolete" — and under any
// narrowing that is not itself retired-only, "X of 64 avatars · N obsolete"
// where X and N are counted off what is actually LISTED (`list`), never off
// the raw fleet. This is the one function that decides all three numbers
// together, so "shown never exceeds total" has a single seam to hold rather
// than being re-derived (and potentially re-broken) at each call site.
export function avatarHeadlineCounts(
  list: readonly LifecycleLike[],
  all: readonly LifecycleLike[],
  statusFilter: readonly string[],
): AvatarHeadlineCounts {
  const total = avatarHeadlineTotal(all, statusFilter);
  if (isRetiredOnlyStatusFilter(statusFilter)) {
    // Every row in `list` already IS retired under this regime — "14 of 14" /
    // "18 of 18" says it all; a "· N obsolete" suffix on top would be noise.
    return { shown: list.length, total, obsoleteShown: 0, retiredOnly: true };
  }
  const shown = list.filter((av) => !isRetiredAvatar(av)).length;
  const obsoleteShown = list.filter((av) => avatarLifecycleStatus(av) === "obsolete").length;
  return { shown, total, obsoleteShown, retiredOnly: false };
}

/**
 * Status filter predicate — a plain `includes` over the DISPLAY label.
 *
 * The lifecycle `deleted` wins over the derived status (IR-1327 r2: a
 * soft-deleted avatar can still derive to `connected`), so a deleted row only
 * ever matches the "Deleted" label. An empty selection matches nothing; the
 * TAB snaps an empty selection back to `DEFAULT_STATUS_FILTER`, so the
 * predicate never sees one in practice (IR-1610 r2).
 */
export function isAvatarVisibleForStatusFilter(
  status: string,
  statusFilter: readonly string[],
  dbStatus?: string,
): boolean {
  const label = dbStatus === "deleted" ? "Deleted" : statusLabel(status);
  return statusFilter.includes(label);
}

// IR-1226 P1-D: "42 / 50" when an effective limit exists, else the bare count.
export function formatCapacity(groupCount: number, limit: number | null | undefined): string {
  return limit ? `${groupCount} / ${limit}` : String(groupCount);
}

// IR-1226 Phase 2: the number measured against the limit is the COUNTED
// usage (groups we still try to collect), not the raw assignment count.
// An older backend that does not send `capacity_used` falls back to the row
// count so the reading never goes blank.
export function capacitySeats(capacityUsed: number | null | undefined, groupCount: number): number {
  return capacityUsed ?? groupCount;
}

// IR-1769 (O-43): the "N / limit still collecting" number is the backend's
// `collecting_seats` (a Stopped avatar is 0 however many rows it holds). It is
// not recomputed here; an older backend falls back to the held count.
export function collectingSeats(
  collecting: number | null | undefined,
  capacityUsed: number | null | undefined,
  groupCount: number,
): number {
  return collecting ?? capacitySeats(capacityUsed, groupCount);
}

// IR-1426 r4 (Rotem, 2026-10-02, Q3 "capacity counts only groups the phone
// lists" → "Yes - only on the phone"): the counting itself is backend-side
// (`capacity.py:capacity_usage`); ops only DESCRIBES it here.
export const CAPACITY_TITLE =
  "groups still being collected / capacity limit — deleted, platform-removed, suspended, Not Relevant and Inactive groups do not take a seat; nor do groups the phone no longer lists (Rotem, IR-1426: capacity counts only groups still on the phone) — manual assignments still do";

// IR-1425 r8 (Rotem, 2026-09-15). The seats line sits under the handset number
// in one cell and carried NO caption, so `14 / 50` read as the group total and
// contradicted the `20 on phone` above it — Tomer filed exactly that on
// 2026-09-15 ("Total number of groups: OPS 14/50, device 8"). Three different
// questions were being answered in one column and only two of them were
// labelled. This is the label for the third.
//
// 🔑 It is a CAPTION, not a tooltip: a hover-only recovery fails the reader who
// is not hovering, which is the reader who filed the bug.
export const CAPACITY_SEATS_CAPTION = "still collecting";

// ─── IR-1426 r4: Groups shows what the phone confirms ───
//
// Rotem, 2026-10-02, on IR-1426 — "Should the column show the phone-confirmed
// number, with the old total on hover?" → "Yes!". E2_1P read 58 while its
// phone confirmed 1: the column answered "what is it SET to cover" and she
// wants "what the phone actually holds". The backend counts it as
// `groups_held` (views_fleet.py, IR-1610 PR #901); this is the display rule.
//
// 🔑 The cell shows ONE number. The assigned total goes on hover, not beside
// it — "with the old total on hover" is the ask, and a second number in the
// cell is exactly the two-numbers-one-column confusion IR-1425 r8 just fixed.
// 🔑 Sorting stays on `group_count` this round (the ticket changed what the
// cell SHOWS, not how the column orders); revisit only if Rotem asks.

export const GROUPS_HEADER_TITLE =
  "How many of this avatar's OPS-assigned groups the phone currently confirms it is in. Hover a cell for the assigned total. A phone that has never reported its group list shows the assigned total. Not a measure of activity.";

export const GROUPS_NEVER_REPORTED_SUFFIX =
  "this phone has never reported its group list, so all are shown";

// `groups_held` is absent (old backend — ops reaches prod first) or null (the
// phone never reported) for two different reasons; both must read exactly as
// the cell did before the field existed, i.e. `group_count`. A bare 0 in
// either case would claim "the phone holds none", which is a different and
// false statement.
export function groupsColumn(av: Pick<FleetAvatar, "group_count" | "groups_held">): {
  shown: number;
  title: string;
} {
  if (typeof av.groups_held === "number") {
    const held = av.groups_held;
    const missing = av.group_count - held;
    // No parenthetical when nothing is missing — and never a negative one
    // (a roster naming more than OPS assigned is a backend reconciliation
    // matter, not something to print as "-3 not on this phone").
    const delta = missing > 0 ? ` (${missing} not on this phone)` : "";
    return {
      shown: held,
      title: `${held} confirmed by the phone · ${av.group_count} assigned in OPS${delta}`,
    };
  }
  return {
    shown: av.group_count,
    title: `${av.group_count} assigned in OPS — ${GROUPS_NEVER_REPORTED_SUFFIX}`,
  };
}

// ─── IR-1425 r8/r9: Delivering is MESSAGES ONLY, with the whole metric beside it ───
//
// Rotem, 2026-09-15: "it should be aligned to the decision in 1555 - so by
// messages only - this is the diffrences between connected. if you can maybe o
// a interim stage and display both: M: [number of groups] , R: [number of
// groups] - similar to groups tab".
//
// r9 (Rotem, 2026-10-01, and the IR-1555 matrix "OPS Counting Rules"): the
// interim `M:`/`R:` shorthand gives way to the metric's own names, the same
// three the Groups tab's activity tiles already use — "Group activity metrics
// must be mutually exclusive per time window: Message = recent message,
// regardless of roster; Roster Only = recent roster and no recent message;
// Observed Total = Message + Roster Only." The window is the rolling 24 hours
// at query time (her answer 1), which is what `delivering_window_days` = 1
// already means. A collector that has not said which kind it delivered stays
// its own bucket — "Missing/unknown reporting must remain a separate state and
// must not be presented as confirmed messages or confirmed no data" — and it
// is NOT inside Observed Total, because a total that silently includes
// unmeasured groups is the "Active Groups" conflation the matrix forbids.
//
// One metric per line, on purpose (`\n`, rendered with `white-space: pre`):
// the first cut of r9 put two metrics per line under `pre-line`, and on the
// real column (Harel's look, 2026-10-01) the column's own width broke them
// again mid-phrase — "Not / reported 217". A name and its number are one unit;
// `pre` keeps each unit whole and the column no wider than its widest line
// (~"Observed Total 217" at 10px).
export const DELIVERY_SPLIT_TITLE =
  "In the last 24 hours, counted at the moment you look: Message = groups that sent a message, whether or not they also sent a member roster · Roster only = groups that sent a member roster and no message · Observed Total = Message + Roster only · Not reported = groups whose collector has not said which kind it delivered (not inside Observed Total)";

/**
 * `Message 4` / `Roster only 2` / `Observed Total 6` — one per line — and
 * `Not reported 3` as a fourth line only while some collector has not reported
 * the kind yet.
 */
export function formatDeliverySplit(
  message: number | null | undefined,
  rosterOnly: number | null | undefined,
  unknown: number | null | undefined,
): string | null {
  // ALL THREE OR NOTHING. They come out of one pass over one population, so a
  // missing sibling does not mean zero — it means this response did not measure
  // the split at all: a detail/PUT (no page-wide pass), or a payload from a
  // backend that predates the split. Rendering `Roster only 0` for either would
  // claim we measured and found none, which is a different and false statement
  // — the same contract the headline's em-dash already keeps.
  if (message === null || message === undefined) return null;
  if (rosterOnly === null || rosterOnly === undefined) return null;
  if (unknown === null || unknown === undefined) return null;
  // Observed Total is the matrix's own sum, never the headline: the headline
  // still carries message + unknown while collectors converge (r8), and the
  // total here must add up from the two lines the reader can see.
  const lines = [
    `Message ${message}`,
    `Roster only ${rosterOnly}`,
    `Observed Total ${message + rosterOnly}`,
  ];
  // Shown ONLY when non-zero: it is a transitional population that drains to 0
  // one collection round after each collector ships the split, and a permanent
  // `Not reported 0` would be noise on every row forever.
  if (unknown) lines.push(`Not reported ${unknown}`);
  return lines.join("\n");
}

/** The expander's per-group wording for the same three states — the metric's
 *  own names, so the list under the row reads in the row's vocabulary. */
export function deliveryKindLabel(
  kind: "message" | "roster_only" | "unknown" | null | undefined,
): string | null {
  if (kind === "message") return "Message";
  if (kind === "roster_only") return "Roster only";
  if (kind === "unknown") return "Not reported";
  return null;
}

// ─── IR-1400: connected vs DELIVERING ───
//
// `group_count` is `COUNT(*) FROM avatar_groups`, the union of four assignment
// sources. `coverage` rows are counted whether or not anything ever arrived, so
// the number answers "how many groups is this avatar connected to" and silently
// includes months-dead groups. Measured on prod 2026-08-19: only 452 of 1,828
// avatar-group links delivered anything inside 7 days, and S2_2 rendered 143
// groups while exactly 1 was still sending data.
//
// r6 (Rotem's reopen, 2026-08-23): the combined "12 of 32" cell was unreadable
// stacked over the capacity line, so delivering renders as its own column —
// Groups keeps the connected count, Delivering carries the bare number, and the
// null case ("delivery not computed") shows "—" rather than a false 0.

// True when the avatar is connected to groups and NONE of them are delivering —
// the shape that means "look at this one": either every group went quiet at once
// or the avatar itself has lost access. One quiet group among many is normal and
// must not be dressed up as an alert.
export function isFullyQuiet(groupCount: number, delivering: number | null | undefined): boolean {
  if (delivering === null || delivering === undefined) return false;
  return groupCount > 0 && delivering === 0;
}

// Per-group line in the expander: the DATE is the load-bearing half. A quiet
// group still says when it last spoke, so an operator can tell "silent since
// Tuesday" from "silent since April" instead of trusting our 7-day threshold.
//
// IR-1441 added the third state, and it is the whole reason this helper is not
// a two-way boolean any more. `delivering` now describes THIS avatar, while the
// date beside it is still the GROUP's. So a row can read Quiet next to a date
// from yesterday — which looks exactly like a bug and is not one: another
// avatar is covering that group. Naming that state "Covered elsewhere" is what
// stops QA reading the correct answer as a regression, and it has to be
// VISIBLE rather than hidden in a title tooltip, because the surprising state
// is precisely the one nobody thinks to hover.
//
// IR-1614 added "archived" on top. The phone shelved the group, and that fact
// wins the pill whatever `delivering` says — but it does not ERASE delivering:
// a group archived on the handset can still be receiving data, and Rotem Ziv's
// stopped-state contract (IR-1606 c63062) is to "show the contradiction and
// actual observation, not a false stopped". So the archived branch sits in
// front of the existing body rather than inside it, and
// `archivedStillDelivering` asks that same body what the row WOULD have read.
export type GroupDeliveryTone = "ok" | "quiet" | "elsewhere" | "stale" | "unknown" | "archived";

export const ARCHIVED_GROUP_DETAIL =
  "archived on this phone — not counted as delivering for this avatar; unarchive on the handset to resume";

type GroupDeliveryRow = {
  delivering?: boolean | null;
  delivering_scope?: string | null;
  last_data_at?: string | null;
  delivering_window_days?: number | null;
  // `undefined` spelled out: a caller spreading `{ archived }` from a loop
  // under exactOptionalPropertyTypes must be allowed to hand us "absent".
  archived?: boolean | null | undefined;
};

/**
 * IR-1425 r10 (Rotem, ROUNDS 4): the TEXT of the per-avatar delivery pill, in
 * words that cannot be read as the group's lifecycle. "Delivering" / "Quiet"
 * sat beside a lifecycle word and were read as one; the pill now says what it
 * measures — a delivery for THIS avatar in ITS window. Window word per
 * `deliveringHeaderLabel` (null → none, 1 → "today", N → "(Nd)").
 * "Unknown" / "Archived" (IR-1614) / stale keep their labels.
 *
 * Text only: `groupDeliveryLabel`'s tones and detail are untouched (other
 * callers — the Delivering column, the strip of old — still read them).
 */
export function deliveryPillText(
  d: { label: string; tone: GroupDeliveryTone },
  windowDays: number | null,
): string {
  const window = windowDays === null ? "" : windowDays === 1 ? " today" : ` (${windowDays}d)`;
  if (d.tone === "ok") return `Delivered${window}`;
  if (d.tone === "quiet" || d.tone === "elsewhere") return `Nothing${window}`;
  return d.label;
}

export function groupDeliveryLabel(row: GroupDeliveryRow): {
  label: string;
  tone: GroupDeliveryTone;
  detail: string;
} {
  // Strictly `=== true`: `false` is measured-and-open, `null`/absent is a
  // collector that has never said (an old collector, Signal, or an OPS ahead
  // of its backend) — unknown, never "not archived", and never a pill.
  if (row.archived === true) {
    return { label: "Archived", tone: "archived", detail: ARCHIVED_GROUP_DETAIL };
  }
  return deliveryLabelIgnoringArchived(row);
}

// True iff the phone archived the group AND the row would otherwise have
// counted as delivering (`ok` or `stale` — the same pair the expander total and
// the Delivering column count). Shares the body below with `groupDeliveryLabel`
// on purpose: a second derivation is how the pill and its note would drift.
export function archivedStillDelivering(row: GroupDeliveryRow): boolean {
  if (row.archived !== true) return false;
  const tone = deliveryLabelIgnoringArchived(row).tone;
  return tone === "ok" || tone === "stale";
}

// ── IR-1707: a member-list read is a REPORT, never ACTIVITY ─────────────────
//
// The expander row rested two things on `delivery_at`, which is "member list
// OR message": the date it showed, and the green of its pill. A collector that
// re-reads every member list every few hours (Signal, 4 h) therefore painted
// every row with today's date and a green pill while the avatar's headline
// said only a fraction had delivered a message — and the reporter read the
// list as message activity. The four helpers below split the two apart: the
// row's date is MESSAGE evidence only, the member-list read gets its own
// labelled line, and the pill keeps its words but is green only when the
// delivery was not a member-list-only one.

type DeliveryKind = "message" | "roster_only" | "unknown" | null | undefined;

const isValidStamp = (iso: string | null | undefined): iso is string =>
  typeof iso === "string" && iso !== "" && !Number.isNaN(Date.parse(iso));

/**
 * IR-1707: when this group last produced a MESSAGE, and who says so.
 * `stored` = the group's last stored message (any covering avatar);
 * `collector` = this avatar's collector reported one that is not stored in
 * this environment (yet). The later of the two wins; an exact tie resolves to
 * `stored`, the stronger claim. Unparsable and absent values are the same
 * thing — an OPS ahead of its backend must never render "Invalid Date".
 *
 * Reads the two message keys and nothing else: a collection stamp ("member
 * list or message") is exactly what this row must stop showing as its date.
 * `at` is the original string.
 */
export function groupLastMessage(row: {
  last_data_at?: string | null | undefined;
  reported_message_at?: string | null | undefined;
}): { at: string | null; source: "stored" | "collector" | null } {
  const stored = isValidStamp(row.last_data_at) ? row.last_data_at : null;
  const collector = isValidStamp(row.reported_message_at) ? row.reported_message_at : null;
  if (stored === null && collector === null) return { at: null, source: null };
  if (collector === null) return { at: stored, source: "stored" };
  if (stored === null) return { at: collector, source: "collector" };
  return Date.parse(collector) > Date.parse(stored)
    ? { at: collector, source: "collector" }
    : { at: stored, source: "stored" };
}

/**
 * IR-1707: when this avatar last read the group's member list — a report, not
 * activity. `null` when the backend does not serve the key (it predates
 * IR-1707) or the value is unusable: unknown, never "none", so the caller
 * renders no line at all rather than a dash.
 */
export function groupRosterReadAt(row: {
  reported_roster_at?: string | null | undefined;
}): string | null {
  return isValidStamp(row.reported_roster_at) ? row.reported_roster_at : null;
}

/**
 * IR-1707: the COLOUR of the per-avatar delivery pill. Identical to the tone
 * mapping the row has always used, plus one rule: a delivery whose only
 * evidence is a member-list read (`roster_only`) keeps its words and loses the
 * green.
 *
 * message + unknown + absent stay green on purpose: the backend's
 * `delivering_headline` = message + unknown, so the number of green pills in
 * the list equals the headline count on the avatar's row. Archived keeps its
 * own colour whatever the kind (IR-1614); stale / unknown read neutral; quiet
 * / elsewhere warn.
 */
export function deliveryPillVariant(
  d: { tone: GroupDeliveryTone },
  kind: DeliveryKind,
): "ok" | "archived" | "idle" | "warn" {
  if (d.tone === "archived") return "archived";
  if (d.tone === "ok") return kind === "roster_only" ? "idle" : "ok";
  if (d.tone === "stale" || d.tone === "unknown") return "idle";
  return "warn";
}

/**
 * IR-1707: the pill's tooltip sentence. On a delivering row the kind says
 * WHICH evidence it was; every other tone keeps its own detail — an archived
 * row must go on explaining the archive, not the kind.
 */
export function deliveryPillDetail(
  d: { tone: GroupDeliveryTone; detail: string },
  kind: DeliveryKind,
): string {
  if (d.tone !== "ok") return d.detail;
  if (kind === "roster_only") {
    return "this avatar read the group's member list in the window — no message arrived";
  }
  if (kind === "message") return "this avatar collected a message within the delivery window";
  return d.detail;
}

export const LAST_MESSAGE_STORED_TITLE =
  "the group's last stored message, from any covering avatar";
export const LAST_MESSAGE_COLLECTOR_TITLE =
  "reported by this avatar's collector in the round ending at this time — the message is not stored in this environment (yet)";
export const LAST_MESSAGE_NONE_TITLE = "no message is stored for this group in this environment";
export const ROSTER_READ_TITLE =
  "when this avatar last read the group's member list — a report, not activity";

function deliveryLabelIgnoringArchived(row: GroupDeliveryRow): {
  label: string;
  tone: GroupDeliveryTone;
  detail: string;
} {
  if (row.delivering == null) {
    return { label: "Unknown", tone: "unknown", detail: "delivery has not been reported" };
  }
  if (row.delivering && (row.delivering_scope === "reported" || !row.last_data_at)) {
    return {
      label: "Delivering",
      tone: "ok",
      detail: "this avatar collected a member list or message within the delivery window",
    };
  }
  if (row.delivering_scope === "reported") {
    return {
      label: "Quiet",
      tone: "quiet",
      detail: "no collector-confirmed delivery within the delivery window",
    };
  }
  if (!row.last_data_at) {
    return { label: "Quiet", tone: "quiet", detail: "no data ever received" };
  }
  if (row.delivering) {
    // The avatar IS collecting this group — but the date beside it belongs to
    // the group, and the two clocks are different facts: `last_delivered_at` is
    // when this avatar last UPLOADED a batch carrying the group,
    // `last_data_at` is when the group last SAID anything. A group that is
    // re-collected while nobody posts in it therefore shows a live pill next to
    // a months-old date — the mirror image of "Covered elsewhere", and just as
    // easy to file as a bug.
    //
    // Naming it is also the thing Rotem actually asked for in IR-1400: "so
    // *this group went quiet* is distinguishable from *this avatar is
    // blocked*". This is the row where those two come apart, so it says which.
    // It still counts as delivering — the avatar did its job — so the expander
    // total and the Delivering column cannot disagree (the IR-1400 r6 rule).
    // Only when we can actually JUDGE the group. `groupIsLive` returns false
    // both for "the group is silent" and for "we were told no window", and
    // conflating those would demote every healthy row during a deploy window
    // where the FE is ahead of the backend.
    if (canJudgeGroup(row) && !groupIsLive(row)) {
      return {
        label: "No new content",
        tone: "stale",
        detail: "this avatar is collecting it; the group itself has posted nothing since",
      };
    }
    return { label: "Delivering", tone: "ok", detail: "last data" };
  }
  // Not this avatar — but is the GROUP still live? Only ask when the answer was
  // actually attributed: on a group-level answer `delivering` already IS the
  // group's own verdict, so there is no second party to attribute it to.
  if (row.delivering_scope === "avatar" && groupIsLive(row)) {
    return {
      label: "Covered elsewhere",
      tone: "elsewhere",
      detail: "another avatar is collecting this group — last data",
    };
  }
  return { label: "Quiet", tone: "quiet", detail: "last data" };
}

// Do we have enough to say anything about the group at all? Both `last_data_at`
// and the window come from the backend, and an FE running ahead of it has
// neither — in which case every row must keep the label it had before.
export function canJudgeGroup(row: {
  last_data_at?: string | null;
  delivering_window_days?: number | null;
}): boolean {
  return (
    typeof row.delivering_window_days === "number" &&
    Number.isFinite(row.delivering_window_days) &&
    !!row.last_data_at &&
    !Number.isNaN(Date.parse(row.last_data_at))
  );
}

// Is the GROUP itself inside the delivering window, whoever fed it? Uses the
// window the backend actually applied rather than a second copy of the
// threshold, so the two can never drift apart.
export function groupIsLive(row: {
  last_data_at?: string | null;
  delivering_window_days?: number | null;
}): boolean {
  if (!row.last_data_at) return false;
  const days = row.delivering_window_days;
  if (typeof days !== "number" || !Number.isFinite(days)) return false;
  const last = Date.parse(row.last_data_at);
  if (Number.isNaN(last)) return false;
  // A future stamp counts as live: sender-clock skew has already put item
  // timestamps ahead of real time (IR-1140) and must not read as gone quiet.
  return last >= Date.now() - days * 86_400_000;
}

// ─── IR-1426: "Collected groups" — what the PHONE says it holds ───
//
// Three numbers now sit next to each other on this row, and they are three
// different populations. Keeping them apart is the whole point of the ticket:
//
//   Groups     `group_count`          — rows in OPS's `avatar_groups`, the union
//                                       of five assignment sources
//   Delivering `groups_delivering`    — how many of those produced data in the
//                                       window (IR-1400)
//   Collected  `reported_group_count` — how many groups the HANDSET listed in
//                                       its latest roster
//
// 🔑 Collected > Groups is NORMAL and is not a warning. WhatsApp emits its
// roster BEFORE `BLACKLIST_GROUPS` is applied and Telegram filters nothing at
// all, so a phone legitimately reports groups OPS never assigned it. Styling
// that gap as a fault would manufacture exactly the "is this broken?" reflex
// IR-1400 spent a round removing — so nothing here returns a warning tone, and
// the caption ("on phone") is what tells the reader whose number this is.

// The cell text. Bare em-dash for BOTH null and absent, never "0":
//   absent — prod runs iris-ops ahead of iris-backend (the ops carve-out), so
//            today's payload carries no such key at all
//   null   — the avatar has never reported (Signal until the VM pull; any
//            unmatched avatar key, forever)
// A 0 would assert "the phone holds no groups", which is a claim we cannot
// make from a field that was never sent. Same null contract the Delivering
// column already keeps.
export function formatCollected(count: number | null | undefined): string {
  return count === null || count === undefined ? "—" : String(count);
}

// ─── IR-1487: the archived count's hover ───
//
// The em-dash needs explaining harder here than it does on Collected groups.
// There, a bare cell means one handset has not reported; here it is the state
// of nearly the whole fleet, because only the V2 Telegram wrapper counts
// archived chats at all. Without the hover, an operator reads a column of
// dashes as a broken column rather than as "nothing else measures this yet".
export function archivedTitle(count: number | null | undefined): string {
  if (count === null || count === undefined) {
    return (
      "No archived count reported.\n" +
      "Only the V2 Telegram collector counts archived chats; every other " +
      "emitter sends no such number, so this is silence rather than a zero."
    );
  }
  const noun = count === 1 ? "chat" : "chats";
  return `${count} archived ${noun} on the phone.\nArchived chats are NOT collected — they are counted separately from the Collected groups figure beside this one.`;
}

// ─── IR-1690 (Rotem, comment 63350): two numbers in ONE Archived column ───
//
// "no separate column — put 2 numbers inside the Archived column, IRIS and
// PHONE, with a tooltip explaining them; mostly the numbers come from the
// phone; and make sure there is no overlap that would show a doubled
// number." She is asking for exactly two readings side by side, never
// summed — a group can be archived in IRIS (`iris_archived_count`,
// `entities.archived_at IS NOT NULL`) AND still physically sit in the
// phone's own Archived folder (`reported_archived_count`, IR-1487) at the
// same time, so adding them would double-count that group.
//
// Null contract matches each source's own rule: `iris` is "—" only when the
// backend has not computed it for this response at all (list responses
// always compute it; a detail/PUT echo might not); `phone` is "—" when the
// handset has never reported (IR-1487 — true of every emitter but the V2
// Telegram wrapper). A measured 0 on either side renders as 0, never "—".
function _archivedSide(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : String(n);
}

export function archivedCellText(
  iris: number | null | undefined,
  phone: number | null | undefined,
): string {
  return `IRIS ${_archivedSide(iris)} · Phone ${_archivedSide(phone)}`;
}

export function archivedCellTitle(
  iris: number | null | undefined,
  phone: number | null | undefined,
): string {
  const irisLine =
    iris === null || iris === undefined
      ? "IRIS archived: not computed on this response."
      : `IRIS archived: ${iris} group${iris === 1 ? "" : "s"} IRIS itself has archived (entities.archived_at is set) for this avatar.`;
  const phoneLine =
    phone === null || phone === undefined
      ? "Phone archived: no archived count reported. Only the V2 Telegram collector counts archived chats; every other emitter sends no such number, so this is silence rather than a zero."
      : `Phone archived: ${phone} archived chat${phone === 1 ? "" : "s"} the handset's own Archived folder reports.`;
  return `${irisLine}\n${phoneLine}\nThese overlap: a group IRIS has archived can still sit in the phone's Archived folder and gets counted in BOTH numbers. They are two independent views, never added together.`;
}

// ─── IR-1433 r4: the tooltip states the SAMPLE TIME, always ───
//
// Rotem, 2026-08-26: "so if you dont print the date you mean it is up to date?
// — I suggest we will have a tooltip with sample time".
//
// She is asking the right question of the wrong surface. The cell shows a date
// ONLY when the reading is stale, so a plain number is an assertion made by
// omission — the reader has to already know the 48-hour rule to decode it, and
// nothing on screen teaches that rule. The number's age is the difference
// between "the phone holds 66 groups" and "the phone held 66 groups in June"
// (IR-1433: two months of exactly that confusion), so the hover now carries the
// stamp on EVERY state, not just the one we chose to warn about.
//
// Three lines, same order every time, so the eye lands in the same place:
//   1. what the number is
//   2. WHEN the phone said it — absolute stamp first, relative age in brackets
//   3. whether that counts as current, and by what rule
//
// Line 3 is the answer to her literal question, printed rather than implied.

/**
 * The absolute sample time for the tooltip — the moment the HANDSET answered,
 * not the moment we received it (`views_fleet._reported_at`, IR-1425 r2).
 *
 * Browser-local, matching every other absolute time in OPS, with the zone
 * spelled out (`26 Aug 2026, 16:46 GMT+3`). The zone is not decoration: the
 * numbers in the Jira thread and in `coverage.jsonl` are UTC, and a bare local
 * time three hours away from those is how a reader concludes the two disagree.
 */
export function formatSampleTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * Relative age for the bracket on line 2. Deliberately derived from
 * `ReportStaleness.hours` — the same number that decides the warning tone — so
 * the tooltip can never say "3h ago" beside an orange stale mark.
 */
export function formatSampleAge(stale: ReportStaleness | null | undefined): string | null {
  const hours = stale?.hours;
  if (hours === null || hours === undefined) return null;
  if (hours < 1) return "under an hour ago";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// Newline-separated: the callers render it in a Radix tooltip with
// `white-space: pre-line`. A single run-on sentence was the old shape and it is
// why the "when" half went unread.
export function collectedTitle(
  count: number | null | undefined,
  reportedAt?: string | null,
  stale?: ReportStaleness | null,
): string {
  if (count === null || count === undefined) {
    return [
      "No reading yet.",
      "This avatar has never reported its phone's group list, so there is no collected count for it.",
    ].join("\n");
  }
  const lines = [`${count} group${count === 1 ? "" : "s"} on the phone.`];
  const sampled = formatSampleTime(reportedAt);
  const age = formatSampleAge(stale);
  if (sampled) lines.push(`Sampled ${sampled}${age ? ` (${age})` : ""}.`);
  lines.push(
    stale?.stale
      ? `Not a current reading — the handset has not reported for ${stale.label}, so the number above is that old.`
      : `Current reading — a plain number means the phone answered within the last ${REPORT_STALE_HOURS} hours.`,
  );
  return lines.join("\n");
}

// ─── IR-1425 r2: is that "on phone" number still a CURRENT reading? ───
//
// It could not go stale before, because the stamp could not stand still. The
// backend fell back to arrival time whenever an incoming roster was not newer
// than the stored one, and the VM monitor re-POSTs its retained
// coverage.jsonl lines every 900s — so every avatar's `reported_groups_at`
// advanced every quarter of an hour whether or not any handset had spoken.
// Measured on prod 2026-08-25: all 26 reporting avatars shared one stamp at
// 12:33:02.265629 and all 26 had moved together to 12:48:16.450434 914s later.
// With `views_fleet._reported_at` fixed, the stamp stands still when nothing
// new arrives — and THAT is what makes this function able to say anything.
//
// It matters most on WhatsApp, which has replayed a roster from on or before
// 25 June since IR-1433: every WA figure on this tab is that old, and until
// now it rendered identically to a reading taken a minute ago.
//
// 48 HOURS, not 24. A scraper appends a roster line per collection cycle and
// those cycles are uneven (Harry3_TG1 alone runs 37–43 min while others are
// hours apart), so a one-day threshold would flag healthy avatars on an ordinary
// slow day. Two full days of silence is unambiguous, and still catches the
// two-month case by a wide margin. The whole point of the caption is that it
// only speaks when something is actually wrong — the same restraint the
// Collected column already exercises by NOT styling collected > assigned as a
// fault.
export const REPORT_STALE_HOURS = 48;

export type ReportStaleness = {
  stale: boolean;
  /** Whole hours since the handset last reported; null when it never has. */
  hours: number | null;
  /** Short human age for the caption — "3d", "61d". Empty when not stale. */
  label: string;
  /** Short calendar date for the caption — "25 Jun". Empty when not stale. */
  date: string;
};

const NOT_STALE: ReportStaleness = { stale: false, hours: null, label: "", date: "" };

/**
 * `reportedAt` is the avatar's `reported_groups_at` (or the monitoring row's
 * `collected_groups_at`). Absent/never-reported is NOT stale: that avatar shows
 * an em-dash rather than a number, and there is no reading to call old. Same
 * contract `groupPhoneMark` keeps for its `unknown` state — silence must not be
 * dressed up as an observation.
 */
export function reportStaleness(
  reportedAt: string | null | undefined,
  now: number = Date.now(),
): ReportStaleness {
  if (!reportedAt) return NOT_STALE;
  const t = new Date(reportedAt).getTime();
  if (Number.isNaN(t)) return NOT_STALE;
  const hours = Math.floor((now - t) / 3_600_000);
  // A future stamp is clock skew (IR-1140), never staleness.
  if (hours < REPORT_STALE_HOURS) return { ...NOT_STALE, hours: Math.max(hours, 0) };
  const days = Math.floor(hours / 24);
  return {
    stale: true,
    hours,
    label: `${days} day${days === 1 ? "" : "s"}`,
    date: new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
  };
}

// ─── IR-1464 r2: a HELD roster says "last known", and says WHY ───
//
// Rotem, 2026-10-01: "if an avatar previously reported groups and suddenly
// reports zero, retain the last known roster with its original timestamp and
// flag the discrepancy. Do not automatically treat the groups as removed [...]
// Show the latest report's time, why it could not be accepted, and the
// timestamp of the last known roster being displayed. [...] the previous
// number is last known information — not a confirmed current count."
//
// IR-1464 r1 taught the backend to HOLD the stamp when a roster matched
// nothing; r2 extends the hold to a sudden zero after a non-zero roster and,
// in both cases, keeps `reported_group_count` at the previous number. That
// makes the number on screen a different KIND of fact from the one the
// "on phone" caption claims: it is what the phone said LAST TIME we believed
// it, not what it says now. E2_1P sat at 11 groups for days after a 0 came in
// and nobody could tell from the cell. This helper is the on-screen half of
// the discrepancy flag; the alert carries the same sentence (A4).
//
// Rendering is deliberately in the stale cell's register (warn tone, caption
// under the number, the whole cell as the tooltip trigger) — it is the same
// message, "this number is older than it looks", with a different cause.

export type RosterRejection = {
  /** Short cell line under the number — `last known · 28 Sep`. */
  caption: string;
  /** Newline-separated tooltip, Rotem's three sentences in her order. */
  title: string;
};

/**
 * `D Mon` in the register the stale caption already uses (`reportStaleness`:
 * en-GB, short month). Newer ICU spells September "Sept" in en-GB; the cell
 * has one column of captions, so pin the three-letter form Rotem wrote
 * ("28 Sep") rather than let one month be four letters wide.
 */
function formatDayMonth(t: number): string {
  return new Date(t)
    .toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    .replace(/Sept\b/, "Sep");
}

/** `D Mon HH:MM`, browser-local like every other absolute time in OPS. */
function formatDayMonthTime(t: number): string {
  const hm = new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${formatDayMonth(t)} ${hm}`;
}

/**
 * Null unless the backend recorded a rejected report that is NEWER than the
 * accepted roster (or there is no accepted roster at all). A rejection older
 * than the stamp means a later report landed and cleared the hold — the
 * backend nulls the columns on landing, but an ops build ahead of that fix,
 * or a replayed payload, must not resurrect a resolved discrepancy.
 */
export function rosterRejection(
  av: Pick<
    FleetAvatar,
    | "roster_rejected_at"
    | "roster_rejected_count"
    | "roster_rejected_reason"
    | "reported_group_count"
    | "reported_groups_at"
  >,
): RosterRejection | null {
  if (!av.roster_rejected_at) return null;
  const rejectedAt = new Date(av.roster_rejected_at).getTime();
  if (Number.isNaN(rejectedAt)) return null;
  const knownAt = av.reported_groups_at ? new Date(av.reported_groups_at).getTime() : Number.NaN;
  if (!Number.isNaN(knownAt) && rejectedAt < knownAt) return null;

  const latest = formatDayMonthTime(rejectedAt);
  const listed = av.roster_rejected_count ?? 0;
  const first =
    av.roster_rejected_reason === "unmatched"
      ? `Latest report (${latest}) listed ${listed} group${listed === 1 ? "" : "s"}, none of them known to IRIS; verification pending.`
      : `Latest report (${latest}) returned 0 groups; verification pending.`;

  const known = av.reported_group_count;
  if (known === null || known === undefined) {
    return {
      caption: "unconfirmed",
      title: [first, "No earlier roster to show.", "Current group count is unconfirmed."].join(
        "\n",
      ),
    };
  }
  const knownDate = Number.isNaN(knownAt) ? null : formatDayMonth(knownAt);
  const second = knownDate
    ? `Showing the last known roster: ${known} group${known === 1 ? "" : "s"}, reported on ${knownDate}.`
    : `Showing the last known roster: ${known} group${known === 1 ? "" : "s"}.`;
  return {
    caption: knownDate ? `last known · ${knownDate}` : "last known",
    title: [first, second, "Current group count is unconfirmed."].join("\n"),
  };
}

// ─── IR-1464 r4: the roster-verification sequence (Rotem 63387; option (b), 65478) ───
//
// "The exit condition for the held state must be a successful, reliable roster
// verification, not 'the phone reports groups again'." The backend stores where
// the avatar is in that sequence (`roster_verification`); there is no scraper
// completion marker yet, so a verified roster says how it was verified. The
// cell shows exactly these labels and adds no other text or state:
//   awaiting_verification  "Awaiting group verification"  (the count is historical)
//   verification_failed    "Verification failed" + the stored reason
//   verified_no_marker     "Verified (no completion marker)"
//   held                   unchanged: "last known · <date>" (rosterRejection)
// NULL / absent is Unknown: it renders nothing here, never "verified", never 0.

export type RosterVerification = {
  /** Short cell line under the number. */
  caption: string;
  /** Newline-separated tooltip. */
  title: string;
  /** `warn` = the number is not a confirmed current count; `ok` = it is. */
  tone: "warn" | "ok";
};

export function rosterVerification(
  av: Pick<
    FleetAvatar,
    | "roster_verification"
    | "roster_verification_reason"
    | "reported_group_count"
    | "reported_groups_at"
  >,
): RosterVerification | null {
  const known = av.reported_group_count;
  const knownAt = av.reported_groups_at ? new Date(av.reported_groups_at).getTime() : Number.NaN;
  switch (av.roster_verification) {
    case "awaiting_verification": {
      // Rotem's own words (63387): "Connected — awaiting group verification",
      // "clearly identify any displayed count as historical".
      const head = "Connected — awaiting group verification.";
      if (known === null || known === undefined) {
        return { caption: "Awaiting group verification", title: head, tone: "warn" };
      }
      const date = Number.isNaN(knownAt) ? "" : `, reported on ${formatDayMonth(knownAt)}`;
      return {
        caption: "Awaiting group verification",
        title: `${head}\nShowing a historical count: ${known} group${known === 1 ? "" : "s"}${date}.`,
        tone: "warn",
      };
    }
    case "verification_failed":
      return {
        caption: "Verification failed",
        title: av.roster_verification_reason?.trim() || "Verification failed.",
        tone: "warn",
      };
    case "verified_no_marker":
      return {
        caption: "Verified (no completion marker)",
        title:
          "Verified: the phone was Connected and two reports at least 30 minutes apart listed the same groups.",
        tone: "ok",
      };
    case "verified":
      return { caption: "Verified", title: "Verified.", tone: "ok" };
    default:
      // held (the "last known" caption owns it), null, absent or a value this
      // build does not know: Unknown is never rendered as verified.
      return null;
  }
}

// ─── IR-1610 r2 — the Reporting filter (Rotem, 2026-08-25) ───
//
// Filters AVATARS (not expander rows) by their roster state: did this phone
// report its group list recently, did it stop, or has it never. Derived from
// `reported_groups_at` through the SAME `reportStaleness` threshold the
// Collected groups cell uses to tag a stale reading, so the chip and the cell
// can never disagree about which avatars "stopped". Labels are the backend's
// COVERAGE_LABELS vocabulary (Title Case).

export type AvatarReportingBucket = "reporting" | "stopped" | "never";

/**
 * Which Reporting bucket an avatar is in, at `now`.
 *   reporting — a stamp within REPORT_STALE_HOURS. A FUTURE stamp counts as
 *               reporting: sender-clock skew has already put stamps ahead of
 *               real time (IR-1140), and a clock fault must not read as
 *               "stopped".
 *   stopped   — a stamp older than REPORT_STALE_HOURS.
 *   never     — null / absent / unparseable: the phone has never told us.
 */
export function avatarReportingBucket(
  av: { reported_groups_at?: string | null },
  now: number = Date.now(),
): AvatarReportingBucket {
  const at = av.reported_groups_at;
  if (!at) return "never";
  if (Number.isNaN(new Date(at).getTime())) return "never";
  return reportStaleness(at, now).stale ? "stopped" : "reporting";
}

export const REPORTING_FILTER_OPTIONS: { value: AvatarReportingBucket; label: string }[] = [
  { value: "reporting", label: "Reporting" },
  { value: "stopped", label: `Stopped Reporting (>${REPORT_STALE_HOURS}h)` },
  { value: "never", label: "Never Reported" },
];

/** Empty selection = no filter — the MultiSelectFilter convention (only Status has explicit defaults). */
export function isAvatarVisibleForReportingFilter(
  av: { reported_groups_at?: string | null },
  selected: readonly string[],
  now: number = Date.now(),
): boolean {
  if (selected.length === 0) return true;
  return selected.includes(avatarReportingBucket(av, now));
}

// ─── IR-1426: the per-group on-phone mark in the expander ───
//
// The tri-state comes from the backend (`views_fleet._annotate_on_phone`) and
// the third state is not decoration: an avatar that never reported has told us
// NOTHING about any of its groups, and rendering that silence as "not on phone"
// would accuse every row on the screen the day before the monitor deploys.
//
// So `unknown` renders as the em-dash the rest of this tab uses for "no value
// here", with the reason in the tooltip — while `false` gets a visible pill,
// because that one IS the finding (S6_1's "Dkr 17").
//
// r2 (Rotem 2026-08-24, "add indication if it is reported by the phone or not
// — 'Reported'"): the labels are HER words now — "Reported" / "Not reported",
// not "On phone" / "Not on phone". One vocabulary across the expander mark, the
// Groups-tab Avatars column and the Reported filter, so the three surfaces
// cannot be read as three different facts. The precise phrasing ("the phone
// listed this group in its latest roster") survives in `detail`, which is what
// the tooltip shows — the pill is the label she asked for, the hover is the
// definition.
export type PhoneMarkTone = "on" | "off" | "unknown";

// The `| undefined` is explicit (not just `?`) for the same reason
// `collectionState` spells it out: the repo compiles with
// exactOptionalPropertyTypes, and the ABSENT case is the one that actually
// happens on prod today — a caller must be able to hand this an explicit
// undefined without a type error.
export function groupPhoneMark(row: { on_phone?: boolean | null | undefined }): {
  label: string;
  tone: PhoneMarkTone;
  detail: string;
} {
  // IR-1610 r2 (Rotem, 2026-09-22): the three labels are the backend's
  // COVERAGE_LABELS vocabulary — Currently Reported / Stopped Reporting /
  // Never Reported — one word set across both repos, so the Groups tab's
  // coverage label and this mark cannot drift apart.
  if (row.on_phone === true) {
    return {
      label: "Currently Reported",
      tone: "on",
      detail: "the phone listed this group in its latest roster",
    };
  }
  if (row.on_phone === false) {
    return {
      label: "Stopped Reporting",
      tone: "off",
      detail: "the phone reported its groups and did not list this one",
    };
  }
  // null — the backend SAID this avatar never sent a roster. Named, but still
  // tone "unknown": silence is not evidence (IR-1228 P4), so it stays muted
  // and never reads as an observation about the group.
  if (row.on_phone === null) {
    return {
      label: "Never Reported",
      tone: "unknown",
      detail: "this avatar has never reported its phone's group list",
    };
  }
  // Absent — the backend hasn't shipped this field yet. We have not been told
  // anything at all, so there is nothing to name.
  return {
    label: "—",
    tone: "unknown",
    detail: "this avatar has never reported its phone's group list",
  };
}

/**
 * IR-1610 r2 — which assigned groups belong in the expander's MAIN list.
 *
 * Rotem: "only groups we actually cover; a group not reported by avatar B but
 * by C shows under C only". Harel: dropped assignments go in a COLLAPSED
 * section under the avatar ("Not on this phone (N)").
 *
 *   heldKnown — at least one row carries `held`. When false (old backend) the
 *               split is not attempted: `onPhone` is today's whole list and
 *               `offPhone` is empty — the honest pre-deploy state.
 *   onPhone   — `held === true` (the phone confirms it), OR `held === null`
 *               (this avatar never sent a roster: silence is not evidence,
 *               IR-1228 P4, so the row stays and is marked "Never Reported"),
 *               OR a manual assignment (an operator's explicit decision is not
 *               for a roster to overrule).
 *   offPhone  — `held === false` and not manual: the phone reported and did
 *               not name it.
 */
export function splitAvatarGroups(groups: FleetAvatarGroup[]): {
  onPhone: FleetAvatarGroup[];
  offPhone: FleetAvatarGroup[];
  heldKnown: boolean;
} {
  const heldKnown = groups.some((g) => g.held !== undefined);
  if (!heldKnown) return { onPhone: groups, offPhone: [], heldKnown };
  const onPhone: FleetAvatarGroup[] = [];
  const offPhone: FleetAvatarGroup[] = [];
  for (const g of groups) {
    if (g.held === true || g.held === null || g.assignment_source === "manual") onPhone.push(g);
    else offPhone.push(g);
  }
  return { onPhone, offPhone, heldKnown };
}

/**
 * IR-1610 r5 (Rotem, 2026-10-02; decision D-1, Harel 2026-10-05): the strip
 * under an avatar breaks the groups LISTED there down by status — "the group
 * count and the breakdown in the strip must match the list".
 *
 * One bucket per backend `lifecycle_label`, verbatim: the label is never
 * mapped, re-cased or derived here. The `lifecycle` WORD is read for two
 * things only — the fixed bucket order, and spotting the archived rows, whose
 * `collection_label` becomes the bracketed sub-split. No other row feeds a
 * `sub`, which is what keeps Stop Pending / Stop Failed out of any stopped
 * bucket (Rotem: "do not include Stop Pending or Stop Failed under Stopped").
 */
export interface GroupStatusBucket {
  key: string;
  label: string;
  count: number;
  sub?: { label: string; count: number }[];
}

export const UNKNOWN_STATUS_BUCKET_KEY = "__unknown__";
export const UNKNOWN_STATUS_BUCKET_LABEL = "status unknown";

// active, then anything suspended, then collection stopped, then archived,
// then the rest; ties fall to the label.
function lifecycleRank(word: string | null | undefined): number {
  if (word === "active") return 0;
  if (word?.includes("suspend")) return 1;
  if (word === "collection_stopped") return 2;
  if (word === "archived") return 3;
  return 4;
}

const COLLECTION_STATE_ORDER = ["stop_pending", "stopped", "stop_failed"];

function collectionRank(state: string | null | undefined): number {
  const i = state ? COLLECTION_STATE_ORDER.indexOf(state) : -1;
  return i === -1 ? COLLECTION_STATE_ORDER.length : i;
}

function byRankThenLabel(
  a: { rank: number; label: string },
  b: { rank: number; label: string },
): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  const la = a.label.toLowerCase();
  const lb = b.label.toLowerCase();
  if (la !== lb) return la < lb ? -1 : 1;
  return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
}

/**
 * The caller passes the rows it renders (the expander's `onPhone`); nothing is
 * filtered here, so `total` and the buckets are always that list. A dangling
 * group id carries its labels as JSON null and an older backend omits them —
 * null, undefined and "" are all "no label" and land in the trailing unknown
 * bucket.
 */
export function summariseGroupStatuses(
  rows: Pick<
    FleetAvatarGroup,
    "lifecycle" | "lifecycle_label" | "collection_state" | "collection_label"
  >[],
): { total: number; anyLabelled: boolean; buckets: GroupStatusBucket[] } {
  const tally = new Map<
    string,
    {
      label: string;
      count: number;
      rank: number;
      sub: Map<string, { count: number; rank: number }>;
    }
  >();
  let unknown = 0;
  for (const row of rows) {
    const label = (row.lifecycle_label ?? "").trim();
    if (!label) {
      unknown += 1;
      continue;
    }
    let bucket = tally.get(label);
    if (!bucket) {
      bucket = { label, count: 0, rank: lifecycleRank(row.lifecycle), sub: new Map() };
      tally.set(label, bucket);
    }
    bucket.count += 1;
    bucket.rank = Math.min(bucket.rank, lifecycleRank(row.lifecycle));
    const subLabel = (row.collection_label ?? "").trim();
    if (row.lifecycle === "archived" && subLabel) {
      const rank = collectionRank(row.collection_state);
      const sub = bucket.sub.get(subLabel);
      if (sub) {
        sub.count += 1;
        sub.rank = Math.min(sub.rank, rank);
      } else {
        bucket.sub.set(subLabel, { count: 1, rank });
      }
    }
  }
  const buckets: GroupStatusBucket[] = [...tally.values()].sort(byRankThenLabel).map((b) => {
    const sub = [...b.sub.entries()]
      .map(([label, s]) => ({ label, count: s.count, rank: s.rank }))
      .sort(byRankThenLabel)
      .map(({ label, count }) => ({ label, count }));
    return sub.length > 0
      ? { key: b.label, label: b.label, count: b.count, sub }
      : { key: b.label, label: b.label, count: b.count };
  });
  if (unknown > 0) {
    buckets.push({
      key: UNKNOWN_STATUS_BUCKET_KEY,
      label: UNKNOWN_STATUS_BUCKET_LABEL,
      count: unknown,
    });
  }
  return { total: rows.length, anyLabelled: tally.size > 0, buckets };
}

/**
 * IR-1610 r5 — the strip as one plain sentence. The render, its aria-label and
 * the tests all read this, so the three cannot say different things. With no
 * labelled row (older backend) it is the count alone.
 */
export function formatGroupStatusSummary(
  summary: ReturnType<typeof summariseGroupStatuses>,
): string {
  const head = `${summary.total} ${summary.total === 1 ? "group" : "groups"} on this phone`;
  if (!summary.anyLabelled) return head;
  const parts = summary.buckets.map((b) => {
    const sub = b.sub ? ` (${b.sub.map((s) => `${s.count} ${s.label}`).join(", ")})` : "";
    return `${b.count} ${b.label}${sub}`;
  });
  return `${head} — ${parts.join(" · ")}`;
}

/**
 * IR-1610 r2 — what to say beside a row in the "Not on this phone" section.
 * Orphan wins (nobody reports it is the stronger fact); then who does; else
 * nothing — a bare row is "the phone dropped it and we know no more".
 */
export function offPhoneAnnotation(
  row: Pick<FleetAvatarGroup, "orphan" | "covered_by">,
): { kind: "orphan" } | { kind: "covered"; by: string[] } | null {
  if (row.orphan) return { kind: "orphan" };
  if (row.covered_by && row.covered_by.length > 0) return { kind: "covered", by: row.covered_by };
  return null;
}

// Cover focus is edited as a comma-separated string, stored as string[] | null.
export function parseCoverFocus(input: string): string[] | null {
  const tags = input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return tags.length > 0 ? tags : null;
}

export function coverFocusToInput(focus: string[] | null | undefined): string {
  return (focus ?? []).join(", ");
}

// ─── IR-1351 r4 item 3: Geo = country multi-select + free text ───
//
// The API field stays ONE free-form string (`geo: string | null`) — these two
// helpers are the seam between that string and the drawer's split UI (country
// picker over the static COUNTRIES list + a free-text input for regions with
// no ISO entry: Levant, Sahel, Gaza…). Keeping the wire format means the
// toolbar's raw-substring search and the table cell need no changes at all.

const COUNTRY_BY_LOWER = new Map(COUNTRIES.map((c) => [c.toLowerCase(), c]));

// Split a stored geo string into { countries, freeText }. A comma-separated
// token that case-insensitively matches a COUNTRIES entry becomes a selection
// (normalized to the canonical casing, so "jordan" seeds the "Jordan" chip);
// everything else re-joins in its original order as the free text.
export function parseGeo(geo: string | null): { countries: string[]; freeText: string } {
  const tokens = (geo ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  const countries: string[] = [];
  const rest: string[] = [];
  for (const t of tokens) {
    const canonical = COUNTRY_BY_LOWER.get(t.toLowerCase());
    if (canonical) countries.push(canonical);
    else rest.push(t);
  }
  return { countries, freeText: rest.join(", ") };
}

// Countries alphabetized (the pick ORDER in a dropdown is noise, so the stored
// string is made deterministic), then the trimmed free text appended. Empty
// inputs → "" — the caller applies `|| null` so a fully cleared Geo unsets the
// column instead of storing an empty string a substring search would match.
export function serializeGeo(countries: string[], freeText: string): string {
  const parts = [...countries].sort((a, b) => a.localeCompare(b));
  const ft = freeText.trim();
  if (ft) parts.push(ft);
  return parts.join(", ");
}

// ─── IR-1307: operator-set IRIS classification(s) ───
//
// Unlike cover_focus (free text, comma-separated), classifications are picked
// from a CLOSED canonical vocabulary the backend serves, so the form holds a
// string[] directly — there is no text to parse and no way to type a value the
// PUT would reject.

// "" is not a thing here: an empty pick means unset, and the backend normalises
// [] to NULL. Send null explicitly so a cleared selection actually clears the
// column instead of being omitted from the payload.
export function classificationsToPayload(selected: string[]): string[] | null {
  const picked = selected.map((s) => s.trim()).filter(Boolean);
  return picked.length > 0 ? picked : null;
}

// Options for the toolbar filter. Prefer the canonical list from the backend so
// every category is filterable even before any avatar carries it; fall back to
// the values actually present in the loaded rows if that request hasn't landed
// (or failed), so the control is never an empty dropdown.
export function classificationFilterOptions(
  canonical: string[] | undefined,
  avatars: { classifications?: string[] | null | undefined }[],
): string[] {
  if (canonical && canonical.length > 0) return canonical;
  const seen = new Set<string>();
  for (const av of avatars) {
    for (const c of av.classifications ?? []) if (c) seen.add(c);
  }
  return [...seen].sort();
}

// Empty filter = show all (the shared OPS multi-select convention). A non-empty
// filter keeps avatars carrying ANY of the selected values, matching the
// backend's any-of `?classification=` semantics — so the client-side filter and
// the API agree if a caller ever uses the param directly.
export function isAvatarVisibleForClassificationFilter(
  classifications: string[] | null | undefined,
  filter: string[],
): boolean {
  if (filter.length === 0) return true;
  const owned = classifications ?? [];
  return filter.some((f) => owned.includes(f));
}

// IR-1696 (Rotem: "the most critical 5 min bug"): fixed 5-person analyst
// roster, initials only (Jake's spec) — matches the backend's
// ASSIGNED_ANALYST_CHOICES exactly, so a value the dropdown offers can never
// be one the PUT then rejects.
export const ASSIGNED_ANALYST_OPTIONS: { value: string; label: string; full: string }[] = [
  { value: "JS", label: "JS", full: "Jake Sanchez" },
  { value: "ST", label: "ST", full: "Sophie" },
  { value: "TS", label: "TS", full: "Tomer" },
  { value: "MH", label: "MH", full: "Mahdi" },
  { value: "RZ", label: "RZ", full: "Rotem Ziv" },
];

// Empty filter = show all (the shared OPS multi-select convention, same as
// Classification). A single-valued field, unlike classifications, so "any of
// the selected initials" collapses to a plain membership test.
export function isAvatarVisibleForAnalystFilter(
  assignedAnalyst: string | null | undefined,
  filter: string[],
): boolean {
  if (filter.length === 0) return true;
  return !!assignedAnalyst && filter.includes(assignedAnalyst);
}

// IR-1225: read-only, auto-derived "is this avatar producing data now?" indicator
// for the Avatars-tab Ingestion column. Mirrors groups-unified-tab's
// collectionStateLabel/collectionStateVariant idiom.
// IR-1697 r9: `unknown` is the ticket's own fourth word ("unavailable/unknown
// evidence"), already printed by the per-environment pair. It is the HEADLINE of
// an avatar whose environments could not be read and hold no receipt: it is never
// "No data" (that means confirmed none) and never a zero. Not a Matrix state.
export type IngestionState = "collecting" | "last_data" | "stopped" | "unknown" | "n/a";

export function ingestionLabel(s: IngestionState | null | undefined): string {
  // IR-1328 (Rotem, 2026-08-10): the value word was "Collecting", which is the
  // neighbouring Collection column's stage name — one column's vocabulary
  // rendered inside another. "Ingesting" matches this column's own header. The
  // wire value stays 'collecting' (backend contract + filter keys).
  if (s === "collecting") return "Ingesting";
  // IR-1327 r8 (Rotem, 2026-08-12, her wording): a landing inside the 12h
  // window on an avatar whose session is DOWN. "Ingesting" is present tense —
  // on S8_1 it sat beside Disconnected and Collection "Stopped", so the row
  // asserted a flow that had already ended. This says what is actually known:
  // this is the last data we got, and it arrived before the avatar went down.
  //
  // IR-1327 r9 widens the state (see `effectiveIngestionState`) to every landing
  // we know about that is not currently flowing — a live session quiet for
  // 12-24h reads this too. The word is unchanged because the claim is unchanged:
  // it names the last data, in the past tense, and asserts nothing about now.
  // The cell prints the AGE next to it so the row says how stale it is without a
  // hover, and the tooltip names which of the two cases produced it.
  if (s === "last_data") return "Last data received";
  // IR-1327 r6: "Stopped" → "No data". The only observation behind this value
  // is *no items landed in 12h* — a quiet group is not a stopped scraper, and
  // the stop-claim misled QA twice (r5). "Stopped" is now Collection's word
  // exclusively, where it means a stop that actually happened.
  //
  // IR-1327 r9: the word is now literal. A row only reaches this state when
  // NOTHING has ever landed from the avatar (or the response carries no landing
  // field at all) — a known-but-old landing reports its age as "Last data
  // received" above, so "No data" no longer covers an avatar we have data from.
  if (s === "stopped") return "No data";
  // IR-1697 r9: the evidence is unavailable, which is not the same claim as none.
  if (s === "unknown") return "Unknown";
  // IR-1351 r12 (Rotem, 2026-08-14, her word): "so its ingestion status should be
  // Not applicable". This was an em-dash — a mark that says only "no value here"
  // and pushes the reason into a tooltip nobody hovers. It also broke the r4
  // invariant this column is otherwise built on: a row rendering a word must be
  // selectable by the chip claiming that word, and the Ingestion filter has said
  // "Not applicable" since IR-1327 r6 while the cell said nothing at all.
  // The cell and the chip now use one word. The tooltip still carries the WHY.
  return "Not applicable";
}

// IR-1351 r12 — the Ingestion column's DERIVED state, the mirror of
// `effectiveStatus` for the Status pill.
//
// `ingestion_state` is computed by the backend, which cannot see this status:
// `ready_to_use` is derived in the browser (r11) and never stored. So a Ready to
// Use avatar arrives carrying whatever the backend concluded — in practice
// 'stopped' ("No data"), which reads as a fault on an avatar nobody ever started.
// Rotem: "running & ingesting is simply not true".
//
// 'n/a' is the honest answer and is already the state that means exactly this —
// "the displayed status already explains the absence, this column makes no
// claim" — which is what the Status pill READY TO USE now does. Note the
// backend's own INGESTION_NO_CLAIM_STATUSES reaches the same conclusion for
// `new`; this is the same rule applied to a status the backend cannot see.
//
// Every consumer (cell, tooltip, filter, sort, donut) must read through this,
// or the r4 invariant breaks in the usual way: a row would render one word while
// the chip claiming that word skipped it.
export function effectiveIngestionState(
  av: {
    status?: string | null | undefined;
    db_status?: string | null | undefined;
    group_count?: number | null | undefined;
    last_data_at?: string | null | undefined;
    ingestion_state?: IngestionState | null | undefined;
  } & EnvHeadlineFields,
): IngestionState | null | undefined {
  return headlineFromEnvironments(av, singleEnvIngestionState(av));
}

/**
 * IR-1697 r9 (F3): the headline is a summary of the two per-environment lines
 * printed under it, so it may not contradict them. The ticket's order:
 *   1. any environment ingesting            -> Ingesting (the backend's 12h
 *      `collecting` is the one "now" signal there is; no window is invented here)
 *   2. else any environment holding a receipt -> Last data received
 *   3. else every environment confirmed none  -> No data
 *   4. else (an environment unreadable)       -> Unknown
 * "No data" is therefore only ever said when nothing was received anywhere. Only
 * the "No data" branch is rewritten: `collecting`, `last_data` and `n/a` already
 * carry a claim the per-environment lines cannot contradict, and an older backend
 * that sends no per-environment fields keeps the single value it always had.
 */
type EnvHeadlineFields = Partial<Omit<EnvIngestionFields, "group_count">> & {
  group_count?: number | null | undefined;
};

function headlineFromEnvironments(
  av: EnvHeadlineFields,
  base: IngestionState | null | undefined,
): IngestionState | null | undefined {
  if (base !== "stopped") return base;
  const parts = envIngestionPairParts(av as EnvIngestionFields);
  if (!parts) return base;
  if (parts.some(envHoldsReceiptHistory)) return "last_data";
  // IR-1697 r10: an environment that could not be checked is the same claim as an
  // unreadable one for the headline: the evidence is unavailable, not absent.
  if (parts.some((p) => p.kind === "unknown" || p.kind === "could_not_check")) return "unknown";
  // Every environment says the avatar has no groups: nothing could be ingested.
  if (parts.every((p) => p.kind === "not_applicable")) return "n/a";
  return base;
}

// An environment "holds a receipt" when the API sent its time, or when it says
// data was ingested there (any group ingested, even a partial one) without a time
// yet: a row that has not been backfilled still must not read "No data".
const RECEIPT_HISTORY_STATES = new Set([
  "ingested",
  "messages_ingested",
  "roster_ingested",
  "identity_ingested",
  "already_imported_historically",
  "ingested_after_stop",
]);

function envHoldsReceiptHistory(p: EnvIngestionPart): boolean {
  if (p.receivedAt !== null) return true;
  if (p.kind === "partial") return true;
  return p.kind === "plain" && RECEIPT_HISTORY_STATES.has(p.state ?? "");
}

/**
 * The time the headline's "Last data received" speaks of: the newest of the
 * avatar's own landing time and each environment's saved receipt, or null.
 */
export function headlineReceiptAt(
  av: { last_data_at?: string | null | undefined } & EnvHeadlineFields,
): string | null {
  const times = [av.last_data_at, av.prod_last_ingested_at, av.dev_last_ingested_at].filter(
    (t): t is string => typeof t === "string" && !Number.isNaN(Date.parse(t)),
  );
  if (times.length === 0) return null;
  return times.reduce((a, b) => (Date.parse(b) > Date.parse(a) ? b : a));
}

function singleEnvIngestionState(av: {
  status?: string | null | undefined;
  db_status?: string | null | undefined;
  group_count?: number | null | undefined;
  last_data_at?: string | null | undefined;
  ingestion_state?: IngestionState | null | undefined;
}): IngestionState | null | undefined {
  if (isReadyToUseAvatar(av)) return "n/a";
  // IR-1327 r9 (Rotem, 2026-08-13): "Some Avatars in this weird status: Running
  // but no data". Her row was CONNECTED / Collection "Running" (green) /
  // Ingestion "No data" (amber) — two columns reading the SAME timestamp
  // (`last_data_at`, served raw by `views_fleet.avatars_list_create` and handed
  // to both) through two different windows: Collection's 24h (her "lets take a
  // day") and Ingestion's 12h. Every avatar quiet for 12-24h therefore renders a
  // green "it is running" beside an amber "nothing arrived". Prod probe on
  // 2026-08-18: 1 of 55 live avatars sat in that band (Farhad(2)_TG(1), 19.2h)
  // and the set rotates daily — it is the window pair, not a bad row.
  //
  // The fix keeps BOTH approved thresholds and removes the contradiction by
  // making this column state what it actually knows. "No data" now means what it
  // says — nothing has EVER arrived — and any landing we do know about, but that
  // is not currently flowing, reports itself as the LAST data (r8's wording, the
  // one Rotem chose herself: "Last data received is my term"). A row can then
  // never claim data is absent while its neighbour is counting the same data as
  // recent; it says how old that data is instead.
  //
  // `last_data_at` is strictly `=== null` for "no data ever observed" and ABSENT
  // on a degraded response that carries no such field (the heartbeat-unavailable
  // path). Both keep the backend's 'stopped' → "No data", for the same reason
  // `isReadyToUseAvatar` draws that line: absence of a field is not evidence.
  if (av.ingestion_state === "stopped" && av.last_data_at != null) return "last_data";
  return av.ingestion_state;
}

export function ingestionVariant(s: IngestionState | null | undefined): "ok" | "inactive" | "idle" {
  if (s === "collecting") return "ok";
  if (s === "stopped") return "inactive";
  // 'last_data' takes the neutral grey dot deliberately: green is this kit's
  // health signal (r4/r6) and a dead session is not health, while the yellow
  // 'inactive' belongs to "No data" — the row that has nothing at all. This
  // one is a statement about the past, so it carries no live signal either way.
  return "idle";
}

// IR-1327 r7 — the em-dash has to say WHY, and it has to name the reason this
// row is showing rather than an either/or.
//
// Rotem, 2026-08-11: "Why for some Disconnected Avatars we have ingestion status
// (S3_2) and for some its not displayed? S3_1, S7_1 — what triggers the
// ingestion status?" She could not answer that from the screen, and the tooltip
// did not help: it read "never linked or operator-parked", offering both reasons
// and committing to neither, on a row whose pill said DISCONNECTED (so neither
// reason was visible anyway). The backend half of r7 fixed the split — 'n/a' now
// keys on the DISPLAYED status — which means the dash can finally be explained
// by pointing at the pill sitting next to it.
//
// r8 adds the third answering state. Rotem asked for the tooltip explicitly:
// "Last data received is my term - with a tooltip explaining that this is the
// last data we had before disconnection" (2026-08-12). `whenText` is the same
// relative wording the Last Seen column uses, so the tooltip can say WHEN
// without this helper knowing how to format a date.
// IR-1327 r9 — the displayed statuses on which a landing is necessarily in the
// past because the session behind it is gone. `deleted`/`blocked`/`cooldown` are
// deliberately absent: those never reach the 'last_data' branch at all, because
// the backend parks them at 'n/a' (INGESTION_NO_CLAIM_STATUSES) before any
// landing is considered.
const SESSION_DOWN_STATUSES = new Set(["disconnected", "obsolete"]);

export function ingestionTitle(
  s: IngestionState | null | undefined,
  status?: string,
  whenText?: string | null,
): string {
  if (s === "collecting") return "Items landed within the last 12h";
  if (s === "last_data") {
    const when = whenText ? ` ${whenText}` : "";
    // IR-1327 r9: the state now covers two cases, and the tooltip must name the
    // one this row is in rather than asserting a disconnection that may not have
    // happened. The r8 sentence stays word-for-word on the dead-session case —
    // it is Rotem's own ("with a tooltip explaining that this is the last data
    // we had before disconnection") — and the live-session case says the session
    // is up, which is what makes it consistent with a Collection column that is
    // still reading "Running" off the same landing.
    // IR-1769 (O-18): a Stopped avatar is neither "disconnected" nor "session
    // still up"; it was stopped on purpose and nothing new is being collected.
    if (status === "stopped") {
      return `The last data we received${when} — the avatar was stopped, so nothing new is being collected.`;
    }
    return SESSION_DOWN_STATUSES.has(status ?? "")
      ? `The last data we received${when} — it arrived before the avatar disconnected. Nothing has come in since.`
      : `The last data we received${when} — the session is still up, but nothing has arrived since.`;
  }
  if (s === "unknown") {
    return "Unknown — at least one environment could not be checked, and no data receipt is known for this avatar";
  }
  if (s === "stopped") {
    // IR-1327 r9: literal now — a known landing of any age reports as
    // "Last data received" above, so reaching this means we hold none at all.
    return "No data — nothing has ever arrived from this avatar";
  }
  // 'n/a' — post-r7 this is reachable ONLY when the Status pill already carries
  // the explanation, so name that status instead of listing possibilities.
  switch (status) {
    case "new":
      return "Never linked — nothing to ingest yet";
    // IR-1351 r12: the caller passes `effectiveStatus`, so this is the pill the
    // operator can see next to the dash-replacement word — which is the whole
    // r7 invariant: an em-dash (now "Not applicable") may only appear on a row
    // whose Status pill already explains it.
    case "ready_to_use":
      return "Ready to Use — no groups assigned and no data has ever arrived, so there is nothing to ingest yet";
    case "blocked":
    case "cooldown":
      return "Suspended — not expected to be ingesting";
    case "deleted":
      return "Deleted — not expected to be ingesting";
    default:
      // Defensive: a status the backend added without updating this map. Say
      // only what is true rather than inventing a reason.
      return "No claim — see the Status column";
  }
}

// ─── IR-1327 r3: Ingestion + Collection become sortable and filterable ───
//
// Rotem asked for both (2026-08-05). They were the only two rendered columns
// with neither a sort control nor a filter, which is backwards: they are the two
// columns an operator scans for trouble. Everything below is deliberately pure
// so the ordering and the filter semantics are unit-testable without mounting
// the tab.
//
// Sort is SEVERITY-RANKED, not alphabetical. Sorting these two on their rendered
// label would order them "Ingesting < Stopped" and "Active < Stop failed <
// Stop pending < Stopped" — orderings that carry no operational meaning. The
// rank puts healthiest first, so ascending reads best→worst and one more click
// (descending) brings the avatars that need attention to the top, which is the
// whole reason to sort this column. Ranks are contiguous ints so a new state can
// be slotted in without renumbering the rest.
export function ingestionSortRank(s: IngestionState | null | undefined): number {
  if (s === "collecting") return 0;
  // IR-1327 r8: a dead session whose data is hours old sits between the two —
  // worse than a live feed, better than a row with nothing at all, and it is
  // the most recoverable failure on the screen (it was working this morning).
  if (s === "last_data") return 1;
  if (s === "stopped") return 2;
  // IR-1697 r9: evidence unavailable ranks just below a confirmed "No data".
  if (s === "unknown") return 2.5;
  return 3; // 'n/a' and anything unrecognised — no claim either way, sorts last
}

export function collectionSortRank(s: CollectionState): number {
  switch (s) {
    // IR-1351: a never-linked avatar is the least actionable row on the screen —
    // nothing was started, so nothing can have gone wrong with it. Ascending
    // still reads best→worst, so one descending click keeps surfacing trouble.
    case "not_initiated":
      return 0;
    case "running":
      return 1;
    case "running_no_data":
      return 2;
    // IR-1769 r5 (plan 3.5): Unknown sits just below Running-no-data, above the
    // stop group, so an unresolved row surfaces on a descending click. Not
    // collecting follows it: it is a proven failure, but not an operator
    // command in flight.
    case "unknown":
      return 3;
    case "not_collecting":
      return 3.5;
    case "stop_pending":
      return 4;
    // IR-1720: ties with stop_pending on purpose — both are an operator command
    // in flight that the collection machine has not yet been seen to act on.
    case "resume_pending":
      return 4;
    case "stopped":
      return 5;
    case "stop_failed":
      return 6; // the VM refused the stop, so it may still collect
    // IR-1720: ties with stop_failed — both mean "the collection machine
    // refused an operator command", the worst thing this column can say.
    case "resume_failed":
      return 6;
    // IR-1720: every state is now an explicit case, so a state added to the
    // union later fails the `never` assignment below at compile time instead of
    // silently inheriting the worst rank through a `default`.
    default: {
      const unreachable: never = s;
      return unreachable;
    }
  }
}

// Filter options carry the STATE as the value and the human word as the label.
// The status/platform filters above key on their display label because those
// predate this pattern; keying on the state here means the filter cannot drift
// if a label is ever reworded. 'n/a' renders as an em-dash in the column, which
// is unusable as a chip, so the option spells out what the dash means (it is the
// same sentence the cell's tooltip already gives).
export const INGESTION_FILTER_OPTIONS: { value: IngestionState; label: string }[] = [
  { value: "collecting", label: "Ingesting" },
  // IR-1327 r8: its own chip, not folded into either neighbour — the r4
  // invariant is that a row visibly rendering a word is selectable by the chip
  // claiming that word, and "Last data received" is a word of its own now.
  { value: "last_data", label: "Last data received" },
  // IR-1327 r6: "Stopped" → "No data". The old word claimed a process was
  // stopped from an input that only ever observed *no items in 12h* — the exact
  // defect this ticket kept finding in the Collection column, and it misled
  // Rotem twice (a quiet-but-healthy TG avatar "Stopped"; "all avatars appear
  // as Ingestion Stopped"). It also collided with Collection's own "Stopped",
  // which r6 gives real meaning; only one column may render that word.
  { value: "stopped", label: "No data" },
  // IR-1697 r9: the headline word of an avatar whose evidence is unavailable.
  { value: "unknown", label: "Unknown" },
  { value: "n/a", label: "Not applicable" },
];

// IR-1769 r5 (Rotem 65061, red team r4 M6): the chips key on the STATE, one to
// one. The bucket alias that made `disconnected` / `retired` render and filter as
// "Stopped" is gone: a row visibly showing a word is selectable by the chip
// claiming that word because the cell, the chip and the predicate all read the
// same `collectionState`, and no function maps one state to another's word.
export const COLLECTION_FILTER_OPTIONS: { value: CollectionState; label: string }[] = [
  // IR-1327 r4: label tracks collectionLabel (asserted in the helper tests), so
  // the chip can never claim a word the column stopped using.
  { value: "not_initiated", label: "Not initiated" },
  { value: "running", label: "Running" },
  { value: "running_no_data", label: "Running, no data" },
  // IR-1769 r5 (Rotem 65061): the two evidence-gated words. Both are listed even
  // when no row carries them, so a zero is on the screen (Matrix Law rule 1:
  // Unknown is never zero-by-omission or hidden).
  { value: "not_collecting", label: "Not collecting" },
  { value: "unknown", label: "Unknown" },
  { value: "stop_pending", label: "Stop pending" },
  { value: "stopped", label: "Stopped" },
  { value: "stop_failed", label: "Stop failed" },
  // IR-1720: appended AFTER the stop trio, each its own chip (neither shares a
  // word with another state, so neither needs a bucket). Chip order is the
  // donut legend order (`CONNECTED_COLLECTION_BUCKETS`), so the two new rows
  // land at the end of the legend rather than reshuffling the six she knows.
  { value: "resume_pending", label: "Resume pending" },
  { value: "resume_failed", label: "Resume failed" },
];

// Empty filter = show all (the shared OPS multi-select convention).
//
// A missing/unknown ingestion_state is treated as 'n/a', matching what the
// column renders for it — otherwise an avatar visibly showing "—" would vanish
// under the very chip that claims to select those rows.
export function isAvatarVisibleForIngestionFilter(
  state: IngestionState | null | undefined,
  filter: string[],
): boolean {
  if (filter.length === 0) return true;
  return filter.includes(state ?? "n/a");
}

export function isAvatarVisibleForCollectionFilter(
  state: CollectionState,
  filter: string[],
): boolean {
  if (filter.length === 0) return true;
  // IR-1769 r5: the chip value IS the state; no bucket, no alias.
  return filter.includes(state);
}

// IR-1287: read-only "did the operator's stop actually reach the scraper VM?"
// indicator for the Avatars-tab Collection column. An operator set two Telegram
// avatars to `disconnected`; the OPS write landed but nothing reached the VM, so
// they kept collecting for 21h with the badge still reading CONNECTED and no way
// to see the stop had not taken effect. The backend now emits a deactivation
// sidecar on a genuine operator stop and records the VM's confirmation.
//
// `status_source === 'operator_stop'` is the ONLY sentinel that means "an
// operator stopped collection". It is deliberately distinct from the generic
// 'manual', which the phone-edit flow also leaves behind — treating 'manual' as
// a stop would flag every hand-edited avatar as stopped.
//
// IR-1351 item 6 (Rotem, 2026-08-09): "when 'New' is the Avatar state the
// collection should read 'Not initiated' instead of Not stopped - what is 'not
// stopped' even mean?". Both halves are answered here. 'not_initiated' is a
// SEPARATE state rather than a second label for 'active' because the column's
// three other consumers — the sort rank, the filter chips and `canIssueStop` —
// all need to tell the two apart; a label-only fork would leave a never-linked
// avatar offering a Stop button.
// IR-1351 r8 (superseded by IR-1769 r5 below): `retired` once forked out of
// `active` so an Obsolete handset did not read RUNNING. It rendered the word
// "Stopped"; that is the claim IR-1769 r5 removes.
//
// IR-1769 r5 (Rotem 65061): `disconnected` and `retired` are GONE from the union.
// They existed only to render the word "Stopped" for an account that was down or
// a handset marked Obsolete, with no stop on record; that is the false claim
// Rotem rejected ("Collection Stopped requires reliable confirmation of the
// applicable stop ... Disconnected, Obsolete or Deleted alone is insufficient").
// They are replaced by two evidence-gated states and nothing else:
//   not_collecting  an authentication failure is RECORDED (the IR-1700 stamp) and
//                   no collection is recorded after it
//   unknown         collection state cannot be established; the cell says why
//                   and what the last evidence was
export type CollectionState =
  | "not_initiated"
  | "running"
  | "running_no_data"
  | "not_collecting"
  | "unknown"
  | "stop_pending"
  | "stopped"
  | "stop_failed"
  // IR-1720: the read-back of the "Resume collection" command. `resume_pending`
  // holds from the press until the avatar's OWN collection activity is newer
  // than the press; `resume_failed` when the collection machine reported it
  // could not start the avatar. Two separate states (not labels) for the reason
  // `not_initiated` is one: sort rank, filter chips, the donut and
  // `canIssueStop` all need to tell them apart from Running and from Stopped.
  | "resume_pending"
  | "resume_failed";

// IR-1327 r6 (Rotem, 2026-08-10): "Not stopped is a state we shouldn't be in" —
// approved vocabulary. The old catch-all `active` ("nobody pressed Stop", true
// of a thriving and a dead avatar alike) splits into three states that answer
// the stage question the column's name promises:
//   running          — the account is up and data actually arrived recently
//   running_no_data  — the account is up but nothing has arrived for a day
//   disconnected     — the account is down, so there is nothing to collect;
//                      renders "Stopped" (her word) — the tooltip carries the
//                      distinction from an operator stop
// Her threshold for "no data": "lets take a day - it should be substantial but
// not too long" ⇒ 24h, one number on every platform (TG cycles hourly; WA and
// Signal stream continuously and have no cycle to count in).
export const COLLECTION_NO_DATA_HOURS = 24;

// IR-1769 r5 (plan 3.1 rule 4): statuses that make NO claim about collection.
// The session is down (disconnected), the account is parked (blocked/cooldown),
// or the row is retired (deleted/obsolete). None of them can be read as
// "Running", and none of them is, on its own, evidence either way: they read
// `not_collecting` only with a recorded authentication failure, else `unknown`.
// (`stopped` is not here: it is stop-bearing and `collectionState` returns on the
// stop branch first.)
export const NO_CLAIM_STATUSES: ReadonlySet<string> = new Set([
  "disconnected",
  "blocked",
  "cooldown",
  "deleted",
  "obsolete",
]);

// Every derived status value `collectionState` may receive (backend
// CANONICAL_STATUSES + the legacy `active`). Anything else is Unknown, named.
const RECOGNISED_STATUSES: ReadonlySet<string> = new Set([
  "connected",
  "active",
  "stale",
  "new",
  "ready_to_use",
  "stopped",
  ...NO_CLAIM_STATUSES,
]);

// IR-1720 — "has this avatar collected since Resume was pressed?"
//
// The ONLY thing that lets a resumed avatar read Running again (Harel's
// confirmation decision, 2026-10-05): the collection machine acknowledging the
// start is not proof, the avatar's own activity is. Evidence is the newest of
// the three stamps a collection cycle can move —
//   last_collection_at   the scrape time of the newest batch it delivered
//   reported_groups_at   the phone's own time on its newest accepted roster
//   roster_rejected_at   the phone's own time on a roster we held back (the
//                        collector still demonstrably ran)
// — compared against the press with a STRICT `>`. No tolerance window: the
// three stamps come from three clocks, but a cycle takes minutes, so seconds of
// skew cannot matter, while any tolerance would let activity from BEFORE the
// stop count as proof of the resume.
//
// Missing, null and unparseable stamps are ignored. An unparseable (or absent)
// press time returns false — there is no zero point to measure against, and
// `collectionState` treats that row as not resuming at all.
//
// Accepted residual: a V1 cycle already in flight when Stop was pressed posts
// its roster with the phone's end-of-run time; after a very quick Resume that
// `reported_groups_at` can be newer than the press, so the row may read Running
// one cycle early. Deliberately not closed here.
export function hasCollectedSinceResume(av: {
  collection_resume_requested_at?: string | null | undefined;
  last_collection_at?: string | null | undefined;
  reported_groups_at?: string | null | undefined;
  roster_rejected_at?: string | null | undefined;
}): boolean {
  const resumeAt = Date.parse(av.collection_resume_requested_at ?? "");
  if (Number.isNaN(resumeAt)) return false;
  for (const iso of [av.last_collection_at, av.reported_groups_at, av.roster_rejected_at]) {
    if (!iso) continue;
    const t = Date.parse(iso);
    if (!Number.isNaN(t) && t > resumeAt) return true;
  }
  return false;
}

// The `| undefined` on each field is explicit (not just `?`) because the repo
// compiles with exactOptionalPropertyTypes: an older backend response may carry
// the key with an undefined value, not merely omit it.
export type CollectionInput = {
  status?: string | null | undefined;
  db_status?: string | null | undefined;
  status_source?: string | null | undefined;
  collection_stopped_at?: string | null | undefined;
  collection_stop_error?: string | null | undefined;
  // IR-1769 r5: the stop CLOCK. `undefined` = the backend did not send the key
  // at all (an older backend); `null` = sent, nothing recorded.
  collection_stop_requested_at?: string | null | undefined;
  // IR-1700: the recorded authentication-failure start (Not collecting evidence).
  auth_failure_since?: string | null | undefined;
  auth_failure_reason?: string | null | undefined;
  auth_failure_since_source?: string | null | undefined;
  disconnected_since?: string | null | undefined;
  disconnected_since_source?: string | null | undefined;
  // IR-1720: the four inputs of the resume branch below.
  collection_resume_requested_at?: string | null | undefined;
  collection_resume_error?: string | null | undefined;
  reported_groups_at?: string | null | undefined;
  roster_rejected_at?: string | null | undefined;
  last_data_at?: string | null | undefined;
  last_collection_at?: string | null | undefined;
  ingestion_state?: IngestionState | null | undefined;
  group_count?: number | null | undefined;
  groups_delivering?: number | null | undefined;
  delivering_window_days?: number | null | undefined;
};

// Why a row reads Unknown, or why a Stop pending carries a contradiction note.
// `collectionDetail` turns these into the words; `collectionState` ignores them.
export type CollectionWhy =
  | "no_stamp" // Disconnected, no recorded authentication failure
  | "stamp_contradicted" // a recorded failure, but a NEWER collection
  | "suspended_no_stop" // blocked / cooldown with no stop on record
  | "obsolete_no_stop"
  | "deleted_no_stop"
  | "unrecognised_status"
  | "backend_no_clock" // the backend does not send the stop clock
  | "stop_contradicted" // collected after the stop was confirmed
  | "collecting_since_request"; // a stop request not yet confirmed, collection newer

const parseMs = (iso: string | null | undefined): number => Date.parse(iso ?? "");

// IR-1769 r5 (plan 3.1): the ONE evidence function for the avatar Collection
// column. Precedence, first match wins:
//   1. stop-bearing  -> stop_failed | stopped | stop_pending (| unknown when the
//      backend cannot date the stop)
//   2. resume in flight (IR-1720)
//   3. never initiated (new / Ready to Use)
//   4. statuses with no Collection claim -> not_collecting | unknown
//   5. any unrecognised status -> unknown
//   6. the measured running family
export function collectionAssessment(
  av: CollectionInput,
  nowMs: number = Date.now(),
): { state: CollectionState; why: CollectionWhy | null } {
  // 1. A stop was issued against this avatar (the Stop PUT, a retirement, a
  // delete) or the stored word is Stopped. Whether it LANDED outranks every
  // status the row can derive to (IR-1287), and a stop-bearing row can never
  // reach Not collecting or Unknown from its status (C4).
  if (av.status_source === "operator_stop" || isStoppedAvatar(av)) {
    // An error wins over a stamp: the VM may have stamped a confirmation on an
    // earlier attempt and then failed to apply the current one.
    if (av.collection_stop_error) return { state: "stop_failed", why: null };
    const ack = parseMs(av.collection_stopped_at);
    const reqRaw = av.collection_stop_requested_at;
    const req = parseMs(reqRaw);
    const lastColl = parseMs(av.last_collection_at);
    // No confirmation from the VM yet: a request is outstanding.
    if (!av.collection_stopped_at || Number.isNaN(ack)) {
      return {
        state: "stop_pending",
        why: !Number.isNaN(req) && lastColl > req ? "collecting_since_request" : null,
      };
    }
    // An ack, from a backend that does not send the stop clock at all: Stopped
    // cannot be shown to be the ack of THIS stop (prod-order guard, rule 1b).
    if (reqRaw === undefined) return { state: "unknown", why: "backend_no_clock" };
    // Stamped only by the VM callback for a stop that carries operator_stop
    // provenance; the stored `stopped` without it cannot be confirmed (the
    // callback ignores it), and an ack with no clock or older than the clock
    // belongs to an earlier stop.
    if (av.status_source !== "operator_stop" || Number.isNaN(req) || ack < req) {
      return { state: "stop_pending", why: null };
    }
    // Contradicted: the collector scraped after it said it had stopped. A batch
    // scraped BEFORE the ack and ingested later has an older scrape time and does
    // not trigger this (65012). The state stays Stop pending (a mark, not a new
    // state), the contradiction goes in the detail line and the alert path.
    if (lastColl > ack) return { state: "stop_pending", why: "stop_contradicted" };
    return { state: "stopped", why: null };
  }
  // 2. IR-1720: a resume in flight. Placed directly after the stop branch and
  // BEFORE every status fork below, because both of the readings it replaces are
  // false claims on a row that was resumed a minute ago:
  //   * "Running" — the 24h rule further down would say it for any avatar that
  //     collected within a day of being stopped, i.e. before it has collected
  //     anything since the resume;
  //   * a down status — the derived `status` can still be `disconnected` until
  //     the first heartbeat after the restart.
  //
  // All three conditions are required:
  //   status_source === 'resume'   written only by the Resume action; a relink,
  //                                a new stop or a park overwrites it, and the
  //                                row then reads whatever that says.
  //   db_status === 'active'       a phone-number edit AFTER a resume stores
  //                                `disconnected` (needs re-pairing) and leaves
  //                                the provenance behind.
  //   a parseable press time       an older backend (and the DEV registry
  //                                mirror) sends none, so the branch never
  //                                fires there and the screen is unchanged.
  //
  // Evidence newer than the press ⇒ fall through to the measured states,
  // untouched. Evidence OUTRANKS the error: a collector that demonstrably ran
  // after the press is running, whatever an earlier start report said.
  if (
    av.status_source === "resume" &&
    av.db_status === "active" &&
    !Number.isNaN(Date.parse(av.collection_resume_requested_at ?? "")) &&
    !hasCollectedSinceResume(av)
  ) {
    return { state: av.collection_resume_error ? "resume_failed" : "resume_pending", why: null };
  }
  // 3. IR-1351: `status === 'new'` is the canonical "prepared but never linked"
  // value — the same one `isAvatarFailing` already excludes on the grounds that
  // never linked is not failed. Read it off the avatar rather than taking a
  // second argument so all call sites (filter, sort, cell) stay a single
  // `collectionState(av)` and cannot pass different statuses.
  if (av.status === "new") return { state: "not_initiated", why: null };
  // IR-1351 r12 (Rotem, 2026-08-14): a Ready to Use avatar has no groups and has
  // never collected anything, so the 24h window below would file it as
  // `running_no_data` on stock that was never started. It is the same claim the
  // column already makes for `new`. IR-1769 r5: this check now sits BEFORE the
  // no-claim statuses, so a stored-Ready-to-Use avatar with a proven auth
  // failure (dev avatar 120) still reads Not initiated, exactly as before.
  if (isReadyToUseAvatar(av)) return { state: "not_initiated", why: null };
  const status = av.status ?? "";
  // 4. Statuses that make no Collection claim. Not collecting needs a recorded
  // authentication failure on a Disconnected row and no collection after it;
  // everything else is Unknown, with the reason named.
  if (NO_CLAIM_STATUSES.has(status)) {
    if (status === "disconnected") {
      const stamp = parseMs(av.auth_failure_since);
      if (Number.isNaN(stamp)) return { state: "unknown", why: "no_stamp" };
      const lastColl = parseMs(av.last_collection_at);
      if (lastColl > stamp) return { state: "unknown", why: "stamp_contradicted" };
      return { state: "not_collecting", why: null };
    }
    if (status === "blocked" || status === "cooldown") {
      return { state: "unknown", why: "suspended_no_stop" };
    }
    return {
      state: "unknown",
      why: status === "obsolete" ? "obsolete_no_stop" : "deleted_no_stop",
    };
  }
  // 5. Closed world: a non-empty status this column does not know is Unknown.
  // null / undefined / "" (a partial payload) keeps the measured path below.
  if (status !== "" && !RECOGNISED_STATUSES.has(status)) {
    return { state: "unknown", why: "unrecognised_status" };
  }
  // 6. The measured running family.
  // IR-1425: collection can succeed while content goes to another environment.
  // Only a positive count measured over this column's 24h window proves Running;
  // a legacy seven-day or unknown window cannot establish that recency.
  if (av.delivering_window_days === 1 && (av.groups_delivering ?? 0) > 0) {
    return { state: "running", why: null };
  }

  // The account is up (connected/stale) — split on whether the COLLECTOR has
  // recent evidence. IR-1663 (Rotem, comment 62958): Collection means "when
  // the collector last collected" and is the same fact on dev and prod;
  // `last_collection_at` carries that, independent of whether the content it
  // collected landed in THIS environment (`last_data_at`, the Ingestion axis,
  // judged separately by `effectiveIngestionState`/12h).
  const split = (iso: string | null): CollectionState => {
    if (iso === null) return "running_no_data";
    const age = nowMs - Date.parse(iso);
    return age <= COLLECTION_NO_DATA_HOURS * 3600_000 ? "running" : "running_no_data";
  };
  if (av.last_collection_at !== undefined)
    return { state: split(av.last_collection_at), why: null };
  // Older backend without the field: fall back to `last_data_at`, then the 12h
  // ingestion boolean — this is a documented approximation, not the axis this
  // column means to show, kept only so a degraded response doesn't crash.
  if (av.last_data_at !== undefined) return { state: split(av.last_data_at), why: null };
  // data within 12h is certainly within 24h; its absence can't distinguish
  // 12-24h quiet from longer, so err on surfacing the avatar as quiet.
  return { state: av.ingestion_state === "collecting" ? "running" : "running_no_data", why: null };
}

export function collectionState(av: CollectionInput, nowMs: number = Date.now()): CollectionState {
  return collectionAssessment(av, nowMs).state;
}

// IR-1769 r5 (Rotem 65061 C3): "Where collection state cannot be established,
// show Unknown with the reason and last known evidence." The reason and the last
// evidence for the states that need them, as plain sentences. `fmt` is the tab's
// date formatter, passed in so this stays a pure helper. null = no extra line
// (every other state keeps its own tooltip in the cell).
//
// DRAFT wording for Rotem (plan 3.5): the family of the Groups tab phrase
// "Unknown - the collector did not report what it checked".
export function collectionDetail(
  av: CollectionInput,
  fmt: (iso: string) => string,
  nowMs: number = Date.now(),
): { reason: string; evidence: string } | null {
  const { state, why } = collectionAssessment(av, nowMs);
  const lastColl = av.last_collection_at ? fmt(av.last_collection_at) : "never recorded";
  const evidence = `Last collected ${lastColl}.`;
  if (state === "not_collecting") {
    const reason = av.auth_failure_reason ? `${av.auth_failure_reason}, ` : "";
    return {
      reason: `Not collecting: authentication failed (${reason}since ${
        av.auth_failure_since ? fmt(av.auth_failure_since) : "not recorded"
      }); no collection recorded since.`,
      evidence,
    };
  }
  if (state === "unknown") {
    switch (why) {
      case "no_stamp": {
        const since = av.disconnected_since
          ? `Disconnected since ${fmt(av.disconnected_since)}${
              av.disconnected_since_source ? ` (${av.disconnected_since_source})` : ""
            }`
          : "Disconnected (start not recorded)";
        return {
          reason: `${since}; no stop on record and no proof that collection stopped.`,
          evidence,
        };
      }
      case "stamp_contradicted":
        return {
          reason: `Authentication failure recorded ${
            av.auth_failure_since ? fmt(av.auth_failure_since) : "earlier"
          }${av.auth_failure_reason ? ` (${av.auth_failure_reason})` : ""}, but collection at ${
            av.last_collection_at ? fmt(av.last_collection_at) : "a later time"
          } is newer.`,
          evidence,
        };
      case "suspended_no_stop":
        return {
          reason: "Suspended; no stop on record. Collection state not established.",
          evidence,
        };
      case "obsolete_no_stop":
        return {
          reason: "Obsolete; no stop on record. Collection state not established.",
          evidence,
        };
      case "deleted_no_stop":
        return {
          reason: "Deleted; no stop on record. Collection state not established.",
          evidence,
        };
      case "backend_no_clock":
        return {
          reason: "The stop was recorded but this backend does not report when it was requested.",
          evidence,
        };
      case "unrecognised_status":
        return { reason: `Status ${av.status ?? ""} is not recognised.`, evidence };
      default:
        return { reason: "Collection state not established.", evidence };
    }
  }
  if (state === "stop_pending" && why === "stop_contradicted") {
    return {
      reason: "Stop failed: still collected after the stop was required.",
      evidence: `Confirmed ${
        av.collection_stopped_at ? fmt(av.collection_stopped_at) : "earlier"
      }; ${evidence.charAt(0).toLowerCase()}${evidence.slice(1)}`,
    };
  }
  if (state === "stop_pending" && why === "collecting_since_request") {
    return {
      reason: "Stop requested, not yet confirmed; collecting since the request.",
      evidence,
    };
  }
  return null;
}

// IR-1327 r4 (Rotem, 2026-08-06): "how can a 'disconnected Avatar' have an
// 'active collection'? how do you judge 'collecting'? especially when the avatar
// has no groups". She was right, and the honest answer is that this column never
// judged collecting at all.
//
// `collectionState` above is `status_source !== 'operator_stop'` and nothing
// else. "Active" therefore meant only NO OPERATOR HAS PRESSED STOP — a default
// every avatar sits in from birth — while the label, the green dot and the
// tooltip ("Collecting normally") all read as an OBSERVATION. On her screenshot
// that produced a DISCONNECTED telegram avatar with 0/60 groups and no data in
// 12h asserting it was collecting normally. Nothing was measured to say that.
//
// So the column states its own scope: it is the stop-command channel, and the
// ONE column that judges data flow is Ingestion — `items landed in the last
// 12h`, which on those same rows already read Stopped, correctly.
//
// r5 (Rotem, 2026-08-09) rejected the r4 header: "its not 'stop control' - its
// 'Collection'". The header went back to the stage name and the columns now run
// Status → Collection → Ingestion, her process order.
//
// r6 (Rotem, 2026-08-10) shipped the value vocabulary she approved: "Not stopped"
// is gone. IR-1769 r5 (Rotem 65061) SUPERSEDES the part of r6 that made
// `disconnected` render "Stopped" too: "Stopped" is now the label of a confirmed
// stop and nothing else; a Disconnected row reads Not collecting (recorded
// authentication failure) or Unknown.
// "Running" is now a measured claim (data within 24h), which is what earns it
// the green dot that r4 took away from the unmeasured "Active".
export function collectionLabel(
  s: CollectionState,
):
  | "Not initiated"
  | "Running"
  | "Running, no data"
  | "Not collecting"
  | "Unknown"
  | "Stop pending"
  | "Stopped"
  | "Stop failed"
  | "Resume pending"
  | "Resume failed" {
  switch (s) {
    case "not_initiated":
      return "Not initiated";
    case "running":
      return "Running";
    case "running_no_data":
      return "Running, no data";
    // IR-1769 r5 (Rotem 65061): the two evidence-gated words.
    case "not_collecting":
      return "Not collecting";
    case "unknown":
      return "Unknown";
    case "stop_pending":
      return "Stop pending";
    case "stopped":
      return "Stopped";
    case "stop_failed":
      return "Stop failed";
    // IR-1720: worded parallel to "Stop pending" / "Stop failed" — the same
    // command grammar read in the other direction. These two words are NEW to
    // the column (Rotem approved the six above in IR-1327 r6, not these).
    case "resume_pending":
      return "Resume pending";
    case "resume_failed":
      return "Resume failed";
    // IR-1769 r5: every state is an explicit case; `Stopped` is the label of
    // `stopped` ONLY. A state added to the union later fails the `never`
    // assignment below at compile time instead of inheriting a word.
    default: {
      const unreachable: never = s;
      return unreachable;
    }
  }
}

// IR-1327 r4 took the green dot away from the unmeasured "Active"; r6 gives it
// back to `running` ONLY — which is now a measured claim (data landed within
// 24h), so green is once again this kit's health signal and nothing else.
// `running_no_data` is amber (up but quiet for a day — worth a look);
// `disconnected` shares `inactive` with an operator stop because both render
// "Stopped"; one word, one look.
export function collectionVariant(s: CollectionState): "ok" | "idle" | "warn" | "inactive" | "err" {
  switch (s) {
    case "running":
      return "ok";
    case "running_no_data":
      return "warn";
    // IR-1769 r5: Not collecting is a recorded failure, so it takes the grey
    // `inactive` dot the stopped family uses; Unknown is neither a claim of
    // health nor of failure, so it stays neutral (`idle`).
    case "not_collecting":
      return "inactive";
    case "unknown":
      return "idle";
    case "stop_pending":
      return "warn";
    case "stopped":
      return "inactive";
    case "stop_failed":
      return "err";
    // IR-1720: same dots as the stop pair they mirror — amber for a command in
    // flight, red for one the collection machine refused. The WORD carries the
    // difference (and the tooltip the detail); colour is never the only signal.
    case "resume_pending":
      return "warn";
    case "resume_failed":
      return "err";
    case "not_initiated":
      return "idle"; // a never-used avatar is not a warning
    default: {
      const unreachable: never = s;
      return unreachable;
    }
  }
}

// Per-avatar capacity override: positive integer or null (→ platform default).
export function parseCapacityLimit(input: string): number | null {
  const n = Number(input.trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

// ─── IR-1327 r4: say how many rows the current filter returned ───
//
// Rotem, 2026-08-06: "I think that we need to add a count for the results".
// A count did exist — but as muted `iris-meta` text wedged between the Columns
// dropdown and the Capacity-defaults button, on a toolbar that now carries
// seven controls. Her screenshot has it on screen ("9 of 56 avatars") and it
// still did not answer her question, which is the finding: at that size and in
// that position it is not readable as the answer to "how many did I just
// filter down to".
//
// The KPI tiles deliberately do NOT move with the filters. They are the
// fleet-wide partition r3 built (connected + stale + disconnected + other ==
// Total, by construction); recounting them per filter would make four of five
// tiles read 0 whenever a Status chip is applied and re-open the very
// "do these numbers add up?" question that round closed. So the filtered count
// gets its own line instead of being smuggled into the tiles.

export interface AvatarFilterState {
  search: string;
  platform: string[];
  status: string[];
  classification: string[];
  analyst: string[];
  ingestion: string[];
  /** IR-1697 r10: the per-environment Ingestion filters. Optional: absent = unset. */
  ingestionProd?: string[];
  ingestionDev?: string[];
  collection: string[];
  /** IR-1610 r2: the Reporting chip — counts as one filter when non-empty. */
  reporting: string[];
  failedOnly: boolean;
}

// How many FILTER CHIPS are narrowing the list right now.
//
// IR-1412 (Rotem, 2026-08-18): "Search doesnt change the filters! its just add
// number of results indication above the table". Search used to count as a
// filter here, which was the r4 reading of it — it narrows the list, so the
// user should be told something is narrowing it. She is drawing the opposite
// line and it is the better one: a chip is a state you have to remember to
// clear, whereas the search box shows its own query back to you. So search now
// contributes the "Showing x of y" half of the line and nothing to the chip
// count.
export function avatarFilterCount(f: AvatarFilterState): number {
  return (
    (f.platform.length > 0 ? 1 : 0) +
    // IR-1610 r2: the defaults are a real selection, so "active" means
    // "not the defaults" — including defaults + Deleted. A widened view is
    // still something "Clear filters" has to undo.
    (isDefaultStatusFilter(f.status) ? 0 : 1) +
    (f.classification.length > 0 ? 1 : 0) +
    (f.analyst.length > 0 ? 1 : 0) +
    (f.ingestion.length > 0 ? 1 : 0) +
    ((f.ingestionProd?.length ?? 0) > 0 ? 1 : 0) +
    ((f.ingestionDev?.length ?? 0) > 0 ? 1 : 0) +
    (f.collection.length > 0 ? 1 : 0) +
    (f.reporting.length > 0 ? 1 : 0) +
    (f.failedOnly ? 1 : 0)
  );
}

// Unfiltered reads as a plain total ("56 avatars") — "Showing 56 of 56" is
// noise. Narrowed always spells out both numbers, so a surprisingly small
// result set is self-explaining rather than looking like missing data (the
// reflex this ticket has triggered twice).
//
// IR-1412: `hasSearch` is separate from `filterCount` because the two answer
// different questions. Searching narrows the list (so both numbers are worth
// spelling out) without leaving a chip to clear (so nothing is "active").
// `total` is now always the whole fleet — see `matchesAvatarSearch`.
//
// IR-1727 follow-up: `obsoleteShown` adds the "· N obsolete" suffix (omitted
// when 0 — e.g. the retired-only regime passes 0 on purpose, see
// `avatarHeadlineCounts`) so a view that still lists obsolete rows says so
// instead of leaving `shown`/`total` to look like they undercount the table.
export function avatarResultLabel(
  shown: number,
  total: number,
  filterCount: number,
  hasSearch = false,
  obsoleteShown = 0,
): string {
  const suffix = obsoleteShown > 0 ? ` · ${obsoleteShown} obsolete` : "";
  if (filterCount === 0 && !hasSearch) {
    return `${total} avatar${total === 1 ? "" : "s"}${suffix}`;
  }
  const shownOf = `Showing ${shown} of ${total} avatars`;
  if (filterCount === 0) return `${shownOf}${suffix}`;
  return `${shownOf} · ${filterCount} filter${filterCount === 1 ? "" : "s"} active${suffix}`;
}

// ─── IR-1412: search narrows the TABLE, never the statistics ───
//
// Rotem, 2026-08-18: "OPS - Avatar search should not change the statistics
// section!". Searching a phone number zeroed all six tiles, emptied both
// donuts, and left the footer reading "Showing 0 of 0 avatars".
//
// The cause was that search was the one narrowing control on this tab that ran
// SERVER-side (`?search=`), while platform/status/classification/ingestion/
// collection/failed-only all filter the fetched array. The tiles count that
// fetched array, so a search re-fetched the fleet AS the search result and the
// statistics silently became statistics-about-the-search. The r4 comment above
// promises "the KPI tiles deliberately do NOT move with the filters" — it was
// true of every chip and false of the search box.
//
// Moving the match here makes search behave like every other control: one
// fetch of the whole fleet, all narrowing client-side, tiles counted off the
// full list. It also removes a request per keystroke against an endpoint that
// re-derives every avatar's status on each call.
//
// Field set mirrors the backend's `apply_avatar_search` (name / phone_number /
// notes / geo, plus the '+'-stripped query for the two identity fields, since
// phone rows carry a leading '+' inconsistently). `device_name` is the one
// addition: the Avatar column renders `device_name || name`, and a match the
// operator cannot SEE reads as a broken search (the IR-1233 r6 rule).
export function matchesAvatarSearch(
  av: {
    name?: string | null;
    device_name?: string | null;
    phone_number?: string | null;
    notes?: string | null;
    geo?: string | null;
  },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const has = (v: string | null | undefined, needle: string) =>
    !!v && v.toLowerCase().includes(needle);
  const identity = [av.name, av.device_name, av.phone_number];
  if (identity.some((v) => has(v, q)) || has(av.notes, q) || has(av.geo, q)) return true;
  // Only the identity fields get the '+'-stripped query: that normalization
  // exists to reconcile phone formatting, and applying it to prose would make
  // "+foo" silently match text containing "foo".
  const stripped = q.replace(/^[+\s]+/, "");
  return stripped !== q && stripped !== "" && identity.some((v) => has(v, stripped));
}

// IR-1385: which scraper GENERATION an avatar runs — the v1/v2 split of the
// Telegram + WhatsApp migration.
//
// This exists because the two generations are operationally different in ways
// an operator has to know before touching an avatar: v1 identifies an avatar by
// its OPS agent key (`S11_2`), v2 by the bare phone number (`447348159060`), and
// the Relink button could not drive v2 at all until IR-1383. Until now the only
// way to answer "which one is this?" was to read the running process on the
// scraper VM.
//
// 🔑 The whole point of the return shape is that `scraper_version` is populated
// for almost nobody. The backend resolves NULL to 'v1'
// (`history_window.py:DEFAULT_SCRAPER_VERSION`) because every production avatar
// runs v1 — a safe default for the COLLECTOR, but rendering it as a flat "v1"
// would state as fact something nobody recorded. That is exactly how IR-1327
// went wrong four times over: a rendered value is a claim, and an assumption
// printed in the same ink as a measurement cannot be told apart from one.
//
// So the cell distinguishes three cases and the caller styles `known: false`
// as muted:
//   'v2' | 'v1' set  → solid, someone recorded this
//   NULL/empty       → "v1 (default)", muted — the fallback, not a fact
//   Signal           → em dash, muted — see `applicable` below
export type ScraperGeneration = {
  /** Exactly what the cell prints. */
  label: string;
  /** The fleet row actually records a version. False ⇒ we are showing a fallback. */
  known: boolean;
  /** The platform has a v1/v2 notion at all. False ⇒ the question is meaningless. */
  applicable: boolean;
};

// Signal is deliberately excluded: the v1/v2 split describes the TG + WA
// migration, and Signal has only ever had the one scraper. `platform_history_defaults`
// carries no Signal cell for the same reason. Printing "v1 (default)" there
// would invent a generation nobody has ever distinguished.
const GENERATIONAL_PLATFORMS = new Set(["telegram", "whatsapp"]);

export function scraperGeneration(
  scraperVersion: string | null | undefined,
  platform: string | null | undefined,
): ScraperGeneration {
  if (!GENERATIONAL_PLATFORMS.has((platform ?? "").trim().toLowerCase())) {
    return { label: "—", known: false, applicable: false };
  }
  const raw = (scraperVersion ?? "").trim();
  if (!raw) return { label: "v1 (default)", known: false, applicable: true };
  // The serializer canonicalizes to lowercase 'v1'/'v2', but these are FLEET
  // tables — written out-of-band by RunSQL and by hand (IR-1205), so a value the
  // API would have rejected can still be sitting in the column. Show whatever is
  // really there rather than silently normalizing it into a generation it may
  // not be; a surprising badge is a bug report, a hidden one is not.
  const normalized = raw.toLowerCase();
  if (normalized === "v1" || normalized === "v2") {
    return { label: normalized, known: true, applicable: true };
  }
  return { label: raw, known: true, applicable: true };
}

// ─── Connected-population donuts (IR-1351 r10) ──────────────────────────────
//
// Rotem, 2026-08-13: "instead 'Fleet Mix' show Connected per collection status —
// to see right away if i have collected avatars which aren't collecting data …
// and than you can remove the connected box", plus "Show Connected per Ingestion
// status — to see right away if i have collected avatars which aren't ingesting
// data".
//
// The r9 Fleet-mix donut answered "what is the fleet made of". That question is
// already answered by the tiles beside it, which is why she wants the ring spent
// on the one she cannot answer from the strip at all: an avatar whose session is
// up is counted as Connected whether or not a single item has landed, so
// "Connected 31" hides the split between 31 collectors and 31 dead weights. Both
// donuts therefore slice the SAME population — the Connected bucket — and their
// centre total IS the Connected tile she asked us to remove, which is what keeps
// the strip's arithmetic (connected + stale + disconnected + other === Total)
// readable after the tile is gone.
//
// 🔑 Both are keyed off the exact functions the columns and the filter chips use
// (`collectionState`, and the raw `ingestion_state` the Ingestion column
// renders), never off a second status list — the same welding that keeps
// `isAvatarFailing` from drifting away from `avatarKpiBucket`. A slice and the
// chip that claims it must always select the same rows.

/** The population both donuts slice: the Connected KPI bucket, nothing else. */
export function isConnectedAvatar(status: string): boolean {
  return avatarKpiBucket(status) === "connected";
}
// IR-1351 r11 (Rotem): "they should display all statuses - they only display 2".
//
// r10 charted only the states that can actually occur on a connected avatar and
// dropped the rest, on the reasoning that "a permanent 0 slice is a standing
// invitation to ask why it never moves". Rotem read the absence as a missing
// feature rather than as an impossibility — twice, once after we explained it in
// writing. An explanation that has to be repeated is a worse answer than a zero
// on the screen, so every option the matching filter chip offers now gets a
// legend row, and a state that cannot occur here says so by reading 0.
//
// The rows ARE the chip list (`COLLECTION_FILTER_OPTIONS` / `INGESTION_FILTER_OPTIONS`),
// in chip order, so "all statuses" means exactly the set she can see and select
// — a legend row and a chip can never disagree about either wording or sequence.
export const CONNECTED_COLLECTION_BUCKETS: readonly CollectionState[] =
  COLLECTION_FILTER_OPTIONS.map((o) => o.value);

export type ConnectedCollectionBucket = CollectionState;

// Semantic, matching `collectionVariant`'s reading of the same states: green is
// measurably collecting, amber is up but quiet, red is a stop that failed, blue
// is an operator action in flight or landed. Hex rather than `var()` — SVG fill
// attributes do not resolve custom properties reliably.
export const CONNECTED_COLLECTION_COLORS: Record<ConnectedCollectionBucket, string> = {
  // Structurally 0 on this population (status 'new' is the `other` bucket), so
  // it is the dimmest of the greys — present to be counted, not to be read.
  not_initiated: "#6b7184", // --iris-fg-4 // token-exempt
  running: "#39ff88", // --iris-neon-green // token-exempt
  running_no_data: "#ff9933", // --iris-neon-orange // token-exempt
  // IR-1769 r5: the two evidence-gated rows, on existing palette tokens of their
  // own (r11 rule: every legend row owns its colour).
  not_collecting: "#a855f7", // --iris-neon-purple // token-exempt
  unknown: "#eaff2a", // --iris-neon-yellow // token-exempt
  stop_pending: "#3e8dff", // --iris-neon-blue // token-exempt
  stopped: "#8b93a7", // --iris-fg-3 // token-exempt
  stop_failed: "#ff3355", // --iris-neon-red // token-exempt
  // IR-1720: the resume pair sits in the same FAMILY as the stop pair (cool =
  // an operator action in flight, hot = one the collection machine refused)
  // but on swatches of its own — cyan beside the stop's blue, the kit's
  // critical pink beside the stop's red. Reusing the stop pair's exact hexes
  // would break the r11 rule above that every legend row owns its colour: a
  // "Stop pending" and a "Resume pending" slice, both non-zero, would be two
  // identical arcs. Both hexes are existing palette tokens, not new colours.
  resume_pending: "#22d3ff", // --iris-neon-cyan // token-exempt
  resume_failed: "#ff4dd2", // --iris-neon-pink // token-exempt
};

/**
 * The population both donuts slice.
 *
 * IR-1351 r11: connected MINUS ready-to-use. An avatar with no groups assigned
 * has nothing to collect and nothing to ingest, so charting it would put spare
 * capacity into "Running, no data" and "No data" — the two slices the rings were
 * added to make trustworthy. Its count is not lost: it is the Ready to Use tile.
 */
export function isChartedConnectedAvatar(av: {
  status?: string | null | undefined;
  db_status?: string | null | undefined;
  group_count?: number | null | undefined;
  // IR-1351 r12: threaded so `isReadyToUseAvatar`'s third clause is actually
  // supplied here. It is an OPTIONAL field, so a signature that omits it does
  // not fail to compile — it silently passes `undefined`, which reads as "no
  // data ever" and reinstates the r11 definition for this caller only. Every
  // narrowed signature on this path therefore names it explicitly.
  last_data_at?: string | null | undefined;
}): boolean {
  return isConnectedAvatar(av.status ?? "") && !isReadyToUseAvatar(av);
}

/**
 * Donut rows for the charted-connected fleet by Collection stage.
 *
 * Pure and avatar-array-keyed so it is unit-testable without rendering recharts.
 * Labels come from `collectionLabel`, so the legend can never claim a word the
 * column stopped using — the invariant IR-1327 r4 established for the chips.
 *
 * Every bucket is returned, zeros included (r11) — see the block comment above.
 */
export function connectedCollectionSlices(
  avatars:
    | readonly (Parameters<typeof collectionState>[0] & {
        group_count?: number | null | undefined;
      })[]
    | undefined,
  nowMs: number = Date.now(),
): { bucket: ConnectedCollectionBucket; label: string; count: number }[] {
  if (avatars == null) return [];
  const counts = new Map<ConnectedCollectionBucket, number>();
  for (const av of avatars) {
    if (!isChartedConnectedAvatar(av)) continue;
    // IR-1769 r5: counted through `collectionState` directly. A slice, the cell
    // word and the chip that claims it are the same state, one to one.
    const bucket = collectionState(av, nowMs);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  return CONNECTED_COLLECTION_BUCKETS.map((b) => ({
    bucket: b,
    label: collectionLabel(b),
    count: counts.get(b) ?? 0,
  }));
}

/** Slice color by LABEL — the shape `DonutWithLegend`'s `colorFor` expects. */
export function connectedCollectionColor(label: string): string | undefined {
  const entry = CONNECTED_COLLECTION_BUCKETS.find((b) => collectionLabel(b) === label);
  return entry ? CONNECTED_COLLECTION_COLORS[entry] : undefined;
}

// Two of the four ingestion states cannot hold on a connected avatar, and this
// is a backend fact, not a UI convention (`avatar_status.derive_ingestion_state`):
//   - 'last_data' requires INGESTION_LAST_DATA_STATUSES = {disconnected} — a
//     landing that arrived BEFORE the session went down
//   - 'n/a'       requires INGESTION_NO_CLAIM_STATUSES = {new, blocked, cooldown, deleted}
// r11 charts them anyway, at 0. The impossibility is real but it is ours, not
// the reader's: a row reading 0 answers "why only two?" on the screen, where the
// question is actually asked.
export const CONNECTED_INGESTION_BUCKETS = INGESTION_FILTER_OPTIONS.map(
  (o) => o.value,
) as readonly IngestionState[];

export type ConnectedIngestionBucket = IngestionState;

// The legend takes the CHIP's word, not the column's, and the two differ on
// exactly one state: the Ingestion column renders 'n/a' as an em dash, which is
// legible in a table cell (the row beside it explains itself) and meaningless as
// a legend row. `INGESTION_FILTER_OPTIONS` is also the vocabulary Rotem means by
// "all statuses" — it is the list the chip offers her.
export function connectedIngestionLabel(s: IngestionState): string {
  return INGESTION_FILTER_OPTIONS.find((o) => o.value === s)?.label ?? ingestionLabel(s);
}

export const CONNECTED_INGESTION_COLORS: Record<ConnectedIngestionBucket, string> = {
  collecting: "#39ff88", // --iris-neon-green // token-exempt
  // Amber, not red: worth a look, the same reading `ingestionVariant` gives it
  // ('inactive'). Red would claim a failure the observation cannot support — a
  // quiet group is not a broken one.
  // IR-1327 r9: the slice is now "nothing has ever arrived" rather than "nothing
  // in 12h", so a connected-but-quiet avatar moves to the blue `last_data`
  // slice, which states how old its data is. The amber slice is correspondingly
  // rarer and stronger: an avatar we have never received anything from.
  stopped: "#ff9933", // --iris-neon-orange // token-exempt
  last_data: "#3e8dff", // --iris-neon-blue // token-exempt
  unknown: "#eaff2a", // --iris-neon-yellow // token-exempt (Collection's Unknown colour)
  "n/a": "#6b7184", // --iris-fg-4 // token-exempt
};

/**
 * Donut rows for the charted-connected fleet by Ingestion state.
 *
 * Reads the raw `ingestion_state` the column renders, and applies the same
 * "missing means 'n/a'" default `isAvatarVisibleForIngestionFilter` applies — so
 * an older backend that omits the field lands in 'Not applicable' rather than
 * being silently counted as ingesting.
 */
export function connectedIngestionSlices(
  avatars:
    | (readonly {
        status?: string | null | undefined;
        group_count?: number | null | undefined;
        last_data_at?: string | null | undefined;
        ingestion_state?: IngestionState | null | undefined;
        // IR-1697 r9: the slice follows the same headline the column prints, which
        // reads the per-environment fields when they are served.
      }[] &
        EnvHeadlineFields[])
    | undefined,
): { bucket: ConnectedIngestionBucket; label: string; count: number }[] {
  if (avatars == null) return [];
  const counts = new Map<ConnectedIngestionBucket, number>();
  for (const av of avatars) {
    if (!isChartedConnectedAvatar(av)) continue;
    const bucket = effectiveIngestionState(av) ?? "n/a";
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  return CONNECTED_INGESTION_BUCKETS.map((b) => ({
    bucket: b,
    label: connectedIngestionLabel(b),
    count: counts.get(b) ?? 0,
  }));
}

/** Slice color by LABEL — the shape `DonutWithLegend`'s `colorFor` expects. */
export function connectedIngestionColor(label: string): string | undefined {
  const entry = CONNECTED_INGESTION_BUCKETS.find((b) => connectedIngestionLabel(b) === label);
  return entry ? CONNECTED_INGESTION_COLORS[entry] : undefined;
}

// ─── IR-1479: the gap between what the phone holds and what we collect ───
//
// Every figure beside this one is structurally incapable of showing a
// shortfall. "N groups" counts avatar_groups rows, which is add-only
// assignment history and can only ever exceed the phone. "N on phone" is the
// handset's own roster. "N archived" is a subset of the phone's chats that is
// deliberately not collected. So an avatar collecting 4 of the 10 groups its
// phone is in rendered identically to one collecting all 10 — which is how
// IR-1450 reached us through an analyst waiting on named groups, rather than
// through any monitoring signal we own.
//
// The backend derives both directions (`fleet_coverage.coverage_gap`) and the
// two are mutually exclusive. This is display only: never recompute the
// subtraction here, or this tab and the Monitoring panel can drift apart on
// one avatar's number the way IR-1034 did.

/** Should the shortfall figure be shown at all? */
export function hasCoverageGap(count: number | null | undefined): boolean {
  return count !== null && count !== undefined;
}

// 0 is a MEASUREMENT — we compared the two figures and the avatar is collecting
// everything its phone holds — so it renders, exactly as IR-1487's "0 archived"
// does. null is "nobody measured": the whole V1 fleet, which publishes no
// per-cycle count, plus every Signal row. Rendering those as 0 would assert a
// clean bill of health across ~72 of 79 cards on no evidence.
export function coverageGapLabel(count: number | null | undefined): string {
  return hasCoverageGap(count) ? `${count} not collected` : "";
}

// The warning colour rides ON TOP of a label that already carries the meaning
// in words. Colour alone must never be the signal — an operator with a colour
// vision deficiency reads "6 not collected" identically to anyone else.
export function coverageGapIsWarning(count: number | null | undefined): boolean {
  return hasCoverageGap(count) && (count as number) > 0;
}

export function coverageGapTitle(
  notCollected: number | null | undefined,
  onPhone: number | null | undefined,
  collectingNow: number | null | undefined,
): string {
  if (!hasCoverageGap(notCollected)) {
    return (
      "No coverage gap reported.\n" +
      "This needs BOTH a phone roster and a per-cycle count from the " +
      "collector. Only the V2 collectors publish the second one, so for " +
      "every other avatar this is silence rather than a zero."
    );
  }
  const phone = formatCollected(onPhone);
  const collecting = formatCollected(collectingNow);
  if (notCollected === 0) {
    return `Collecting all ${phone} groups the phone reports.\nMeasured against the collector's own count for its last completed cycle (${collecting}).`;
  }
  const noun = notCollected === 1 ? "group" : "groups";
  return `${notCollected} ${noun} on the phone that the last completed cycle did not collect.
The phone reports ${phone}; the collector packaged ${collecting}.
The avatar can report itself perfectly healthy while this is non-zero — a shortfall is invisible to the outcome and failure counts.`;
}

// The other direction, and it is NOT an error to suppress. On WhatsApp the
// roster drops chats this account is not a participant of while the collection
// loop takes every group chat with no membership check, so a collector can
// exceed its own phone — meaning we are collecting from groups the avatar was
// removed from. Shown only when non-zero: it is an anomaly, not a fourth
// always-on figure.
export function overCollectingTitle(count: number | null | undefined): string {
  const noun = count === 1 ? "group" : "groups";
  return `Collecting ${count} more ${noun} than the phone reports being a member of.
The roster leaves out chats this account is no longer a participant of; the collection loop does not check membership. So these are most likely groups the avatar was removed from and we are still collecting.`;
}

// ─── IR-1441 r6: the Delivering column must say WHAT PERIOD it means ───
//
// Rotem, 2026-09-07: the column read simply "Delivering" while its meaning
// moved from "in the last seven days" to "in the last day", and nothing on the
// page said so. "Anyone reading the column tomorrow will not know which
// question it answers."
//
// 🔑 THE LABEL IS DERIVED, NEVER HARDCODED. The window is a backend constant
// and the two environments do not agree: at the time of writing `main`/DEV run
// the 1-day avatar window and `production` still runs 7. A literal "Delivering
// today" would therefore have been FALSE on the very screen Rotem tests. The
// header reads the `delivering_window_days` the response actually carried, so
// it cannot drift from the number beside it — and when the window is promoted
// to prod the header follows on its own, with no second ticket.

export function deliveringWindowDays(
  rows: { delivering_window_days?: number | null }[] | undefined,
): number | null {
  if (!rows?.length) return null;
  for (const r of rows) {
    const d = r.delivering_window_days;
    if (typeof d === "number" && Number.isFinite(d)) return d;
  }
  return null;
}

export function deliveringHeaderLabel(windowDays: number | null): string {
  if (windowDays === null) return "Delivering";
  if (windowDays === 1) return "Delivering today";
  return `Delivering (${windowDays}d)`;
}

export function deliveringHeaderTitle(windowDays: number | null): string {
  const period =
    windowDays === null
      ? "the recency window the backend applied"
      : windowDays === 1
        ? "the last day"
        : `the last ${windowDays} days`;
  // One template literal, not a concatenation: biome's `useTemplate` rejects
  // mixing `+` with an interpolating part, and `noUnusedTemplateLiteral`
  // rejects backticks on the parts that interpolate nothing. Either style alone
  // trips one of the two.
  return `Groups from which this avatar collected a member list or message within ${period}, regardless of which environment ingested the content. Another avatar's collection does not count here.`;
}

// ─── IR-1679 item 4: "Could Not Check" instead of a stale Delivering number ──
//
// Rotem (Jira IR-1679, 2026-09-24): when the nightly delivery rollup is
// stale, show "Could Not Check" rather than the frozen number. Ingestion
// Matrix law rule 4: Could Not Check is not zero and never a stale number
// shown as current, so this label REPLACES the count entirely rather than
// annotating it — showing the number beside "Could Not Check" would still be
// rendering a stale figure as if it meant something.
export const DELIVERING_COULD_NOT_CHECK_LABEL = "Could Not Check";

/** Tooltip text for the "Could Not Check" state: when the rollup last
 * succeeded, or that it has never completed on this environment. */
export function deliveringCouldNotCheckTitle(lastCheckedAt: string | null | undefined): string {
  if (!lastCheckedAt) {
    return "The nightly delivery rollup has not completed on this environment yet, so this avatar's Delivering count cannot be checked.";
  }
  const when = new Date(lastCheckedAt);
  const formatted = Number.isNaN(when.getTime()) ? lastCheckedAt : when.toLocaleString();
  return `The nightly delivery rollup is stale, so the Delivering number cannot be trusted. Last successful check: ${formatted}.`;
}

// ─── IR-1502: the Load column ────────────────────────────────────────────────
//
// Rotem, 2026-09-07 (c61810): "Show the TREND, not the level ... and color it if
// it never drains. to save space you can use a simpler wording and show full
// details in tooltip." The backend words the headline (avatar_load.py
// owed_headline) — rendered VERBATIM here, never recomputed, so the screen and
// the API can never disagree about what a number means.

type LoadLike = AvatarLoad | null | undefined;

/** The cell text. UNKNOWN IS NOT ZERO: no report renders "unknown", never "0". */
export function formatLoad(load: LoadLike): string {
  if (!load || !load.reported || !load.headline) return "unknown";
  return load.headline;
}

/**
 * The colour. "warn" only for a queue that never drained across its readings —
 * and NEVER for a suspected reset: a queue zeroed by a fault looks like a
 * spectacular recovery, the backend withholds the trend, and the colour must not
 * contradict that. Fewer than two readings cannot say, so no colour.
 */
export function loadTone(load: LoadLike): "warn" | null {
  if (!load || !load.reported || load.suspect_reset) return null;
  return load.never_drains === true ? "warn" : null;
}

/** The full details, for the tooltip. Every unknown says WHY it is unknown. */
export function loadTitle(load: LoadLike): string {
  if (!load || !load.reported) {
    return "No capacity report from this avatar yet.\nShown as unknown, not as zero — nobody has measured this queue.";
  }
  const level = load.media_owed == null ? "unknown" : load.media_owed.toLocaleString("en-US");
  const asOf = load.sampled_at ? ` (as of ${load.sampled_at})` : "";
  const lines = [`Owed media: ${level}${asOf}`];
  const readings = `${load.samples} reading${load.samples === 1 ? "" : "s"}`;
  if (load.suspect_reset) {
    lines.push(
      "Trend withheld: the queue fell sharply — possible queue reset.",
      "A sudden large fall is treated as a fault, not as a recovery.",
    );
  } else if (load.per_day == null) {
    lines.push(`Trend: unknown — a rate needs at least two readings (have ${readings}).`);
  } else {
    const rate = `${Math.round(load.per_day) >= 0 ? "+" : ""}${Math.round(load.per_day).toLocaleString("en-US")}/day`;
    lines.push(`Trend: ${rate} over ${Math.round(load.span_hours)}h from ${readings}.`);
    if (load.never_drains === true) lines.push("It never drained across these readings.");
  }
  lines.push(`Status: ${load.status} — thresholds are off until they can be calibrated honestly.`);
  return lines.join("\n");
}

// ─── IR-1697: explicit PROD/DEV Ingestion pair ───

export type EnvKey = "prod" | "dev";

/**
 * IR-1697 screen phase: what one environment's half of the pair shows.
 *  - `absent`:  an older backend sent no opinion at all -> an em dash, as before.
 *  - `unknown`: the environment could not be read (every new field null). Reads
 *    Unknown -- never Partial and never a zero (Ingestion Matrix law: Unknown /
 *    Could Not Check are never zero).
 *  - `could_not_check`: IR-1697 r10 contract, `<env>_ingestion: "could_not_check"`.
 *    This deployment cannot read the environment. Reads Could Not Check -- never
 *    No data, never a zero, never a time.
 *  - `not_applicable`: IR-1697 r10 contract, `<env>_ingestion: "not_applicable"`.
 *    The avatar has zero groups. Reads Not Applicable.
 *  - `partial`: some groups ingested and some failed. `text` is the BACKEND's
 *    `<env>_ingestion_label`, verbatim; this module composes no Partial wording.
 *  - `plain`:   every other state, exactly the label it had before the screen
 *    phase ("No data" for a checked null).
 */
export interface EnvIngestionPart {
  env: EnvKey;
  /** "PROD" | "DEV", the prefix the cell prints. */
  envLabel: string;
  kind: "absent" | "unknown" | "could_not_check" | "not_applicable" | "partial" | "plain";
  text: string;
  /** The per-environment state value served (`<env>_ingestion`), or null/undefined. */
  state?: string | null | undefined;
  /** The failed groups, for the drill-in. Empty unless `kind === "partial"`. */
  failedGroups: EnvIngestionFailedGroup[];
  /**
   * IR-1697 receipt round: when the last ITEM landed in this environment
   * (`<env>_last_ingested_at`, an item receipt -- never the monitoring
   * heartbeat), or null. Only set together with `receiptLabel`.
   */
  receivedAt: string | null;
  /** The backend's word for that receipt (`<env>_receipt_label`), verbatim. */
  receiptLabel: string | null;
}

type EnvIngestionFields = { group_count?: number | undefined } & Pick<
  FleetAvatar,
  | "prod_ingestion"
  | "prod_ingestion_label"
  | "prod_ingestion_summary"
  | "prod_ingestion_failed_groups"
  | "prod_ingestion_partial"
  | "prod_last_ingested_at"
  | "prod_receipt_label"
  | "dev_ingestion"
  | "dev_ingestion_label"
  | "dev_ingestion_summary"
  | "dev_ingestion_failed_groups"
  | "dev_ingestion_partial"
  | "dev_last_ingested_at"
  | "dev_receipt_label"
>;

function envPart(av: EnvIngestionFields, env: EnvKey): EnvIngestionPart {
  const prod = env === "prod";
  const state = prod ? av.prod_ingestion : av.dev_ingestion;
  const label = prod ? av.prod_ingestion_label : av.dev_ingestion_label;
  const summary = prod ? av.prod_ingestion_summary : av.dev_ingestion_summary;
  const failed = prod ? av.prod_ingestion_failed_groups : av.dev_ingestion_failed_groups;
  const partial = prod ? av.prod_ingestion_partial : av.dev_ingestion_partial;
  const envLabel = prod ? "PROD" : "DEV";
  // Receipt round: a time without the API's word for it is not shown.
  const at = prod ? av.prod_last_ingested_at : av.dev_last_ingested_at;
  const word = prod ? av.prod_receipt_label : av.dev_receipt_label;
  const receivedAt = at && word ? at : null;
  const receiptLabel = at && word ? word : null;

  if (state === undefined) {
    return {
      env,
      envLabel,
      kind: "absent",
      text: "—",
      failedGroups: [],
      receivedAt: null,
      receiptLabel: null,
    };
  }
  // IR-1697 r10 contract: the backend now says WHAT an all-null environment is.
  // These two are read before anything else, so a stale `partial`/`summary`
  // beside them can never turn them back into a count or a "No data".
  if (state === "could_not_check") {
    return {
      env,
      envLabel,
      kind: "could_not_check",
      text: "Could Not Check",
      state,
      failedGroups: [],
      receivedAt: null,
      receiptLabel: null,
    };
  }
  if (state === "not_applicable") {
    return {
      env,
      envLabel,
      kind: "not_applicable",
      text: "Not Applicable",
      state,
      failedGroups: [],
      receivedAt: null,
      receiptLabel: null,
    };
  }
  if (partial === true) {
    // A partial flag with no label is a backend fault; say Unknown rather than
    // invent wording or fall back to a state that is not true.
    return {
      env,
      envLabel,
      kind: "partial",
      text: label ?? "Unknown",
      failedGroups: failed ?? [],
      receivedAt,
      receiptLabel,
    };
  }
  // The backend nulls EVERY new field for an environment it could not read.
  // `partial === null` (present, not absent) with no summary is that case.
  //
  // IR-1697 r10 (QA finding 4): until the backend serves `could_not_check` /
  // `not_applicable`, the same all-null shape arrives for an avatar with NO groups
  // (43 of 43 such rows on dev, 2026-10-20) and for an unreadable environment, and
  // the screen cannot tell them apart. The r9 exception that kept "No data" for
  // `group_count === 0` is gone: a null the screen cannot interpret is Unknown,
  // never a confirmed "No data". The contract values above replace this guess
  // as soon as the backend serves them.
  //
  // Receipt round: an env that WAS read and has a receipt is not unknown -- items
  // landed there, and the backend says so in the label it serves.
  if (partial === null && summary == null && receivedAt === null) {
    return {
      env,
      envLabel,
      kind: "unknown",
      text: "Unknown",
      failedGroups: [],
      receivedAt: null,
      receiptLabel: null,
    };
  }
  return {
    env,
    envLabel,
    kind: "plain",
    text: label ?? "No data",
    state,
    failedGroups: [],
    receivedAt,
    receiptLabel,
  };
}

/**
 * Both halves in PROD-then-DEV order, or `null` when the backend sent neither
 * (an older backend: render no line at all).
 */
export function envIngestionPairParts(av: EnvIngestionFields): EnvIngestionPart[] | null {
  if (av.prod_ingestion === undefined && av.dev_ingestion === undefined) return null;
  return [envPart(av, "prod"), envPart(av, "dev")];
}

/**
 * "PROD: Ingested · DEV: Never ingested" -- the explicit, always-visible
 * pairing IR-1697 requires ("Show two explicitly labelled ingestion
 * indicators ... visible together in consistent PROD/DEV order"), mirroring
 * the Groups tab's existing "prod / dev" count-pair convention
 * (`fmtPair` in groups-unified-tab.tsx) rather than hiding DEV in a tooltip.
 *
 * `null` for a field means "looked, no evidence there" and renders as its
 * own word ("No data"), never collapsed into the same em dash as `undefined`
 * ("older backend, has no opinion at all" -- see FleetAvatar's field
 * comments). Returns `null` (render nothing) only when the backend sent
 * neither field, so an older backend's row shows no extra line at all
 * rather than "PROD: — · DEV: —", which would read as a real, checked,
 * empty answer.
 */
export function envIngestionPairLabel(av: EnvIngestionFields): string | null {
  const parts = envIngestionPairParts(av);
  if (!parts) return null;
  return parts.map((p) => `${p.envLabel}: ${p.text}`).join(" · ");
}

/** "7h ago" -- the one relative-age wording of the Avatars and Groups Ingestion cells. */
export function fmtRelative(iso: string | null): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * IR-1697 receipt round: an instant as exact UTC, "2026-10-20 13:35:39 UTC",
 * for the hover title beside the relative age. Empty for an unparseable value.
 */
export function utcInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

/**
 * IR-1697 r13 (Mahdi 66145): the VISIBLE receipt time, the exact instant with its
 * zone first and the relative age after it, "2026-10-29 12:10:19 UTC (2h ago)".
 * An unparseable value keeps today's relative-only text.
 */
export function receiptTimeText(iso: string): string {
  const exact = utcInstant(iso);
  return exact ? `${exact} (${fmtRelative(iso)})` : fmtRelative(iso);
}

// ─── IR-1697 r10 (QA finding 2): ingestion filter, sort and statistics per environment ───
//
// Ticket: "Make ingestion filters, sorting, statistics and exports explicitly
// environment-specific wherever they consume these values." The headline
// (`effectiveIngestionState`) is a summary of the two environments and stays what
// the column prints; everything below reads ONE environment's own half of the
// pair, so "DEV ingesting but PROD historical" can be found, sorted and counted.
// There is no export on the Avatars tab.

/** The headline's four words, plus the contract's Could Not Check. Not a Matrix state. */
export type EnvIngestionBucket = IngestionState | "could_not_check";

/**
 * The "now" window of one environment. The backend's single `ingestion_state` is
 * the only server-side "collecting" signal and it covers the environment this
 * backend runs in, so it cannot be split. An environment is Ingesting here when
 * its own saved receipt (`<env>_last_ingested_at`, an item receipt) is inside the
 * SAME 12h window the backend's `collecting` uses (see `ingestionTitle`).
 */
export const ENV_INGESTING_WINDOW_MS = 12 * 60 * 60 * 1000;

/**
 * IR-1697 r11 (audit fix 1): the displayed statuses on which a receipt is
 * necessarily in the past, because the session behind it is gone or was stopped on
 * purpose (IR-1327 r8 and IR-1769). The pair, its filter, strip and sort must not
 * call such a row "Ingesting", whatever the age of its last receipt.
 */
const SESSION_NOT_COLLECTING_STATUSES = new Set(["disconnected", "obsolete", "stopped"]);

export function envIngestionState(
  av: EnvHeadlineFields & { status?: string | null | undefined },
  env: EnvKey,
  nowMs: number = Date.now(),
): EnvIngestionBucket {
  const parts = envIngestionPairParts(av as EnvIngestionFields);
  const p = parts?.find((x) => x.env === env);
  // An older backend with no per-environment fields has no evidence: Unknown.
  if (!p || p.kind === "absent" || p.kind === "unknown") return "unknown";
  if (p.kind === "could_not_check") return "could_not_check";
  if (p.kind === "not_applicable") return "n/a";
  if (p.receivedAt !== null) {
    const age = nowMs - Date.parse(p.receivedAt);
    if (
      !Number.isNaN(age) &&
      age <= ENV_INGESTING_WINDOW_MS &&
      !SESSION_NOT_COLLECTING_STATUSES.has(av.status ?? "")
    ) {
      return "collecting";
    }
  }
  if (envHoldsReceiptHistory(p)) return "last_data";
  return "stopped";
}

/**
 * The hover text of one environment's value in the pair (IR-1697 r11, audit fix 1).
 * The pair replaced the headline cell, but Rotem's rulings on what that cell's
 * tooltip said still apply to whatever the cell shows, per environment:
 *  - IR-1327 r7: a no-claim value is explained by the Status pill, one reason only;
 *  - IR-1327 r8: a receipt on a dead session says it predates the disconnection;
 *  - IR-1327 r9: "No data" only when nothing ever arrived; a live row whose data is
 *    old is told its session is still up, never that it disconnected.
 * `status` is the row's displayed status. Returns undefined where the value needs
 * no extra text (the older "Could Not Check"/"Unknown" titles stay in the cell).
 */
export function envPartTitle(
  p: EnvIngestionPart,
  av: EnvHeadlineFields & { status?: string | null | undefined },
  status: string,
  nowMs: number = Date.now(),
): string | undefined {
  if (p.kind === "not_applicable") {
    switch (status) {
      case "new":
      case "ready_to_use":
      case "blocked":
      case "cooldown":
      case "deleted":
        return ingestionTitle("n/a", status);
      default:
        return "This avatar has no groups, so there is nothing to ingest.";
    }
  }
  if (p.kind === "plain" || p.kind === "partial") {
    const bucket = envIngestionState(av, p.env, nowMs);
    if (bucket === "stopped") return ingestionTitle("stopped");
    if (bucket === "collecting") return ingestionTitle("collecting");
    if (bucket === "last_data") {
      return ingestionTitle(
        "last_data",
        status,
        p.receivedAt ? receiptTimeText(p.receivedAt) : null,
      );
    }
  }
  return undefined;
}

// One list, offered once for PROD and once for DEV. Same words as the headline
// chip plus the two the contract adds; the value is the bucket, one to one.
export const ENV_INGESTION_FILTER_OPTIONS: { value: EnvIngestionBucket; label: string }[] = [
  { value: "collecting", label: "Ingesting" },
  { value: "last_data", label: "Last data received" },
  { value: "stopped", label: "No data" },
  { value: "unknown", label: "Unknown" },
  { value: "could_not_check", label: "Could Not Check" },
  { value: "n/a", label: "Not Applicable" },
];

export function isAvatarVisibleForEnvIngestionFilter(
  av: EnvHeadlineFields,
  env: EnvKey,
  filter: string[] | undefined,
): boolean {
  if (!filter || filter.length === 0) return true;
  return filter.includes(envIngestionState(av, env));
}

/**
 * Sort value for one environment: healthiest first. Could Not Check and Not
 * Applicable are `null`, which `useClientSort` places after every known value in
 * BOTH directions -- never as 0, never first on a descending click.
 */
export function envIngestionSortValue(s: EnvIngestionBucket): number | null {
  if (s === "collecting") return 0;
  if (s === "last_data") return 1;
  if (s === "stopped") return 2;
  if (s === "unknown") return 3;
  return null;
}

export const CONNECTED_ENV_INGESTION_COLORS: Record<EnvIngestionBucket, string> = {
  ...CONNECTED_INGESTION_COLORS,
  could_not_check: "#22d3ff", // --iris-neon-cyan // token-exempt
};

export function connectedEnvIngestionLabel(s: EnvIngestionBucket): string {
  return ENV_INGESTION_FILTER_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

/** Donut rows for the charted-connected avatars by ONE environment's ingestion. */
export function connectedEnvIngestionSlices(
  avatars:
    | readonly ({
        status?: string | null | undefined;
        group_count?: number | null | undefined;
        last_data_at?: string | null | undefined;
      } & EnvHeadlineFields)[]
    | undefined,
  env: EnvKey,
  nowMs: number = Date.now(),
): { bucket: EnvIngestionBucket; label: string; count: number }[] {
  if (avatars == null) return [];
  const counts = new Map<EnvIngestionBucket, number>();
  for (const av of avatars) {
    if (!isChartedConnectedAvatar(av)) continue;
    const bucket = envIngestionState(av, env, nowMs);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  // Every bucket is listed, so Could Not Check / Not Applicable read 0 on the
  // screen rather than being absent (Matrix law: never zero by omission).
  return ENV_INGESTION_FILTER_OPTIONS.map((o) => ({
    bucket: o.value,
    label: o.label,
    count: counts.get(o.value) ?? 0,
  }));
}

/** Slice color by LABEL, the shape `DonutWithLegend`'s `colorFor` expects. */
export function connectedEnvIngestionColor(label: string): string | undefined {
  const entry = ENV_INGESTION_FILTER_OPTIONS.find((o) => o.label === label);
  return entry ? CONNECTED_ENV_INGESTION_COLORS[entry.value] : undefined;
}

// IR-1697 r17 (red team of r16, F1): the Avatars Status column width, px. It was 471
// (r16) / 453 (r14), set by the Disconnected reason line (ellipsised text up to 240 px
// plus the non-shrinking "since" tail of 203 px). Measured on DEV at 1440x900 the pill
// is at most 115 px and the plain "since" line 160 px; 340 keeps all of them whole,
// leaves about 109 px for the reason text (full text stays in its title), and takes
// "Collected groups" from 52% to 100% visible at default scroll.
export const AVATARS_STATUS_COL_WIDTH = 340;

// IR-1758 r5 (Rotem 65786 option A): the "No groups found — re-checking" warning and
// its next check time, rendered BESIDE the Collection value. Pure text; it never
// feeds collectionState / collectionLabel. A missing next-check time reads Unknown.
export function noGroupsRecheckDetail(
  av: {
    no_groups_warning?: string | null | undefined;
    no_groups_next_check_at?: string | null | undefined;
    no_groups_next_check_source?: "heartbeat" | "estimate" | "unknown" | null | undefined;
  },
  fmt: (iso: string) => string,
): { warning: string; nextCheck: string } | null {
  if (!av.no_groups_warning) return null;
  const at = av.no_groups_next_check_at;
  const nextCheck = at
    ? `next check ${av.no_groups_next_check_source === "estimate" ? "~" : ""}${fmt(at)}${
        av.no_groups_next_check_source === "estimate" ? " (estimate)" : ""
      }`
    : "next check Unknown";
  return { warning: av.no_groups_warning, nextCheck };
}
